// Offer UI adapted from the merchant-supplied prototype; Shopify remains the cart authority.
export function offerEscape(value) { return String(value ?? '').replace(/[&<>"']/g, character => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[character])); }

export function offerCaption(tier, giftCount) {
  const savings = (tier.savingsText ?? 'Save {discount}%').replaceAll('{discount}', tier.discount);
  const gift = giftCount ? (tier.giftText ?? '+ {count} FREE {gift}').replaceAll('{count}', giftCount).replaceAll('{gift}', giftCount === 1 ? 'Gift' : 'Gifts') : '';
  return [savings.trim(), gift.trim()].filter(Boolean).join(' ');
}

export function initialOfferQuantity(offers) {
  return offers.find(offer => offer.preselected === true)?.quantity
    ?? offers.find(offer => offer.quantity === 4)?.quantity
    ?? offers[0]?.quantity
    ?? 4;
}

export function initialProductPairs(tiers, variants, productId, variantId) {
  if (!productId) return new Map();
  const linked = variants.get(variantId);
  const variant = linked?.productId === productId && linked.available ? linked
    : [...variants.values()].find(option => option.productId === productId && option.available);
  if (!variant) return new Map();
  const maximum = Math.max(0, ...tiers.map(tier => tier.quantity));
  return new Map(Array.from({length:maximum}, (_, index) => [index, variant.id]));
}

const SELECTION_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

export function restoreOfferSelection(saved, tiers, variants, addon, now = Date.now()) {
  if (!saved || saved.version !== 1 || !Number.isFinite(saved.savedAt) || saved.savedAt > now || now - saved.savedAt > SELECTION_MAX_AGE || !Array.isArray(saved.pairs)) return null;
  const maximum = Math.max(...tiers.map(tier => tier.quantity));
  const pairs = new Map();
  for (const pair of saved.pairs) {
    if (!Array.isArray(pair) || pair.length !== 2) continue;
    const [index, id] = pair;
    if (Number.isSafeInteger(index) && index >= 0 && index < maximum && typeof id === 'string' && variants.get(id)?.available) pairs.set(index, id);
  }
  return {
    quantity: tiers.some(tier => tier.quantity === saved.quantity) ? saved.quantity : initialOfferQuantity(tiers),
    pairs,
    shoppingAlternatives: saved.shoppingAlternatives === true,
    addonSelected: Boolean(saved.addon?.selected === true && addon.available && saved.addon.id === addon.id && saved.addon.type === addon.type)
  };
}

export function offerQuote(pairs, quantity, variants, tiers, addon = {}) {
  const eligible = [...variants.values()].filter(variant => variant.available);
  const minimum = eligible.length ? Math.min(...eligible.map(variant => variant.price)) : 0;
  let subtotal = 0, chosen = 0;
  for (let index = 0; index < quantity; index++) {
    const variant = variants.get(pairs.get(index));
    if (variant?.available) { subtotal += variant.price; chosen++; }
    else subtotal += minimum;
  }
  return {...addonPricing({subtotal,...tierPricing(subtotal,quantity,tiers)},addon),complete:chosen === quantity,chosen};
}

export function bundleGiftBenefits(gifts, shippingUnlock = 8) {
  const unlock = Number(shippingUnlock);
  return [...gifts.slice(0, 1), {name:'Free shipping', unlock:Number.isInteger(unlock) && unlock > 0 ? unlock : 8, shipping:true}];
}

export function giftProgress(benefits, quantity, tiers) {
  const unlocked = benefits.filter(gift => quantity >= gift.unlock).length;
  const next = tiers.map(tier => tier.quantity).sort((a,b) => a-b).find(count => count > quantity && benefits.some(gift => gift.unlock > quantity && gift.unlock <= count));
  return {unlocked, total:benefits.length, next, nextUnlocksAll:next != null && benefits.every(gift => next >= gift.unlock)};
}
export function existingDiscountCodes(cart) {
  const discounts = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))];
  return [...new Set([...(cart.discount_codes || []).map(discount => discount.code), ...discounts.filter(discount => discount?.type === 'discount_code').map(discount => discount.title)].filter(Boolean))];
}

// Apply shipping codes and safely finish legacy pending attempts that already included a gift.
// New attempts leave wash bag rewards entirely to UpCart.
export async function settleOfferGifts(attempt, getCart, updateCart) {
  if (!attempt?.offerCodes?.length && !attempt?.paidGifts && !attempt?.items?.some(item => item.properties?._BundleGift)) return false;
  let cart = await getCart();
  if (attempt.offerCodes.length) cart = await updateCart({discount:[...new Set([...existingDiscountCodes(cart),...attempt.offerCodes])].join(',')});
  const charged = cart.items.filter(item => item.properties?._BundleRequest === attempt.id && item.properties?._BundleGift && item.final_line_price > 0);
  if (!charged.length) return false;
  cart = await updateCart({updates:Object.fromEntries(charged.map(item => [item.key,0]))});
  if (cart.items.some(item => item.properties?._BundleRequest === attempt.id && item.properties?._BundleGift && item.final_line_price > 0)) throw new Error('Please review the gift prices in your cart.');
  return true;
}

export async function reconcileOfferGifts(attempt, getCart, updateCart) {
  const giftIds = new Set((attempt?.items || []).filter(item => item.properties?._BundleGift).map(item => String(item.id)));
  if (!giftIds.size) return false;
  const cart = await getCart();
  // UpCart can grant the same reward independently. Only remove our duplicate line.
  const rewarded = new Set(cart.items.filter(item => giftIds.has(String(item.variant_id || item.id)) && item.properties?._BundleRequest !== attempt.id && item.final_line_price === 0 && item.quantity > 0).map(item => String(item.variant_id || item.id)));
  const duplicates = cart.items.filter(item => item.properties?._BundleRequest === attempt.id && item.properties?._BundleGift && rewarded.has(String(item.variant_id || item.id)));
  if (!duplicates.length) return false;
  await updateCart({updates:Object.fromEntries(duplicates.map(item => [item.key,0]))});
  return true;
}

const OFFER_GROUPS = [{id:'stripes',label:'Grip Stripes'},{id:'basics',label:'Grip Basics'},{id:'patterns',label:'Patterns'},{id:'limited',label:'Limited Edition'}];
const OFFER_SORTS = [{id:'best-selling',label:'Best sellers'},{id:'created-descending',label:'New arrivals'}];
const OFFER_FILTERS = [{id:'all',label:'All'}, ...OFFER_SORTS, OFFER_GROUPS.find(group => group.id === 'limited'), ...OFFER_GROUPS.filter(group => group.id !== 'limited')];
const FILTER_COLORS = {red:'#b43049',pink:'#feebf1',black:'#161616',blue:'#d7e8f4',green:'#b4bc97',brown:'#46270f',yellow:'#f7f5b8',gray:'#d6d3cd',purple:'#a05290',white:'#fff',orange:'#e7a268',cream:'#f6f0e2',buttercream:'#f5e7c6',burgundy:'#800020',mulberry:'#7c1d3f',matcha:'#8daa68',navy:'#1f3357'};
const FILTER_GROUPS = [
  {id:'colors',label:'Color'},
  {id:'styles',label:'Sock style',options:[['stripes','Stripes'],['solids','Solids'],['patterns','Patterns'],['designs','Designs']]},
  {id:'cuts',label:'Cut',options:[['crew','Crew'],['ballerina','Ballerina']]},
  {id:'availability',label:'Availability',options:[['available','Available now'],['preorder','Pre-order']]}
];
const filterToken = value => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');
export function offerColorFamily(value) {
  const token = filterToken(value).replace(/grey/g,'gray');
  const contains = part => (`-${token}-`).includes(`-${part}-`);
  if (contains('gray')) return 'gray';
  if (['beige','caramel','chocolate','cocoa','coffee','espresso','latte','mocha','oat','tan'].some(contains)) return 'brown';
  return ['buttercream','burgundy','mulberry','matcha','navy',...Object.keys(FILTER_COLORS)].find(contains) || token;
}
export function offerFilterData(variant) {
  let tags = [];
  try { tags = JSON.parse(variant.productTags || '[]'); } catch { /* Old catalog markup remains usable. */ }
  tags = Array.isArray(tags) ? tags.map(filterToken) : [];
  const categoryColors = String(variant.filterColors || '').split(',').map(value => value.trim()).filter(Boolean);
  const colors = [...new Set([...categoryColors.map(offerColorFamily),...tags.map(offerColorFamily).filter(color => FILTER_COLORS[color])])];
  if (!colors.length) {
    const fallback = offerColorFamily(variant.label || variant.title);
    if (FILTER_COLORS[fallback]) colors.push(fallback);
  }
  const text = `${variant.style || ''} ${variant.title || ''}`.toLowerCase();
  const styles = [];
  if (/stripes/.test(text)) styles.push('stripes');
  if (tags.includes('solids') || /basic/.test(text)) styles.push('solids');
  if (tags.includes('pattern') || /pinstripe|checkered|polka/.test(text)) styles.push('patterns');
  if (tags.includes('design') || /print/.test(text)) styles.push('designs');
  const cuts = tags.includes('crew') ? ['crew'] : [];
  if (/ballerina/.test(text)) cuts.push('ballerina');
  return {colors,styles,cuts,availability:variant.available ? [variant.preorder ? 'preorder' : 'available'] : []};
}
export function matchesOfferFilters(variant, filters = {}) {
  const data = variant.filters || offerFilterData(variant);
  return FILTER_GROUPS.every(({id}) => !filters[id]?.length || filters[id].some(value => data[id].includes(value)));
}
export function offerFilterOptions(variants) {
  const data = [...variants].map(variant => variant.filters || offerFilterData(variant));
  const colors = [...new Set(data.flatMap(item => item.colors))];
  const colorOrder = Object.keys(FILTER_COLORS);
  colors.sort((a,b) => (colorOrder.includes(a) ? colorOrder.indexOf(a) : 100) - (colorOrder.includes(b) ? colorOrder.indexOf(b) : 100) || a.localeCompare(b));
  return FILTER_GROUPS.map(group => ({...group,options:group.id === 'colors'
    ? colors.map(id => [id,id === 'gray' ? 'Grey' : id.replace(/(^|-)([a-z])/g,(_m,space,letter)=>(space ? ' ' : '')+letter.toUpperCase())])
    : group.options.filter(([id]) => data.some(item => item[group.id].includes(id)))})).filter(group => group.options.length);
}
export function offerGroup(variant) {
  // Polka dots belong in Patterns even when inherited from the old Limited catalog.
  if (/polka/i.test(variant.style)) return 'patterns';
  if (variant.category === 'limited') return 'limited';
  if (/pin|check/i.test(variant.style)) return 'patterns';
  return /basic/i.test(variant.style) ? 'basics' : 'stripes';
}
export function offerGroupLabel(variant) {
  return OFFER_GROUPS.find(group => group.id === offerGroup(variant)).label;
}
export function rankedOfferOptions(variants, productIds) {
  const rankedProducts = new Set(productIds.map(String));
  return rankVariants([...variants].filter(variant => rankedProducts.has(String(variant.productId))), productIds);
}
export function bestSellerOfferOptions(variants, priorityIds, taggedIds = []) {
  const eligible = new Set([...priorityIds, ...taggedIds].map(String));
  // Match bundle-new: collection members in collection order, then tagged styles in catalog order.
  return rankVariants([...variants].filter(variant => eligible.has(String(variant.productId))), priorityIds);
}

export function offerOptionGroups(ordered, filter, productId, variantId) {
  // Only product URLs supply a featured product. Keep discovery filters in their own order.
  const featured = filter === 'all' && productId
    ? ordered.filter(variant => String(variant.productId) === String(productId)) : [];
  if (variantId) featured.sort((a, b) => Number(String(b.id) === String(variantId)) - Number(String(a.id) === String(variantId)));
  const featuredIds = new Set(featured.map(variant => variant.id));
  const groups = OFFER_GROUPS.filter(group => filter === 'all' || group.id === filter)
    .map(group => ({...group, options:ordered.filter(variant => !featuredIds.has(variant.id) && offerGroup(variant) === group.id)}));
  return featured.length ? [{id:'featured',label:'Featured',options:featured},...groups] : groups;
}
const OFFER_ICONS = {
 check:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 12 4 4 8-8"/></svg>',
 chev:'<svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>',
 remove:'<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="m9 9 6 6m0-6-6 6"/></svg>',
 star:'<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M9.1 2.8a1 1 0 0 1 1.8 0l1.9 3.8 4.2.6a1 1 0 0 1 .6 1.7l-3 3 .7 4.2a1 1 0 0 1-1.5 1L10 15.2l-3.8 1.9a1 1 0 0 1-1.5-1l.7-4.2-3-3A1 1 0 0 1 3 7.2l4.2-.6z"/></svg>',
 seal:'<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m10 2 2.5 1.3 2.8.4.4 2.8L17 9l-1.3 2.5-.4 2.8-2.8.4L10 16l-2.5-1.3-2.8-.4-.4-2.8L3 9l1.3-2.5.4-2.8 2.8-.4Z"/><path d="m6.7 9 2.1 2.1 4.5-4.5"/></svg>',
 lock:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
 ship:'<svg class="ship" viewBox="0 0 64 64" fill="none" stroke="#1c1c1c" stroke-width="2.6" aria-hidden="true"><path d="M6 18h30v24H6z" fill="#F8FEC1"/><path d="M36 26h11l9 9v7H36z" fill="#fff"/><circle cx="17" cy="45" r="5" fill="#fff"/><circle cx="46" cy="45" r="5" fill="#fff"/><path d="M14 26h12M14 32h8"/></svg>'
};

export function setQuantity(selection, id, value) {
  const quantity = Number(value);
  if (!Number.isSafeInteger(quantity) || quantity < 0) return new Map(selection);
  const next = new Map(selection);
  if (quantity) next.set(String(id), quantity);
  else next.delete(String(id));
  return next;
}

export function resizePairs(pairs, quantity) {
  const count = Number(quantity);
  if (!Number.isSafeInteger(count) || count < 1) throw new Error('Enter a whole-number quantity of at least one.');
  return new Map([...pairs].filter(([index]) => Number.isSafeInteger(index) && index >= 0 && index < count));
}

export function choosePair(pairs, index, variant, quantity) {
  // No active pill means browse-only: previewing a SKU must preserve every choice.
  if (index === null) return new Map(pairs);
  if (!Number.isSafeInteger(index) || index < 0 || index >= quantity || !variant?.available) throw new Error('Choose an available style for this pair.');
  const next = new Map(pairs);
  const id = String(variant.id);
  // Choosing a design is an assignment, including when it appears in another filter.
  next.set(index, id);
  return next;
}

export function pairSelection(pairs, quantity, variants) {
  const selection = new Map();
  for (const [index, id] of resizePairs(pairs, quantity)) {
    if (!variants.get(id)?.available) continue;
    selection.set(id, (selection.get(id) || 0) + 1);
  }
  return selection;
}

export function rankVariants(variants, productIds) {
  const rank = new Map(productIds.map((id, index) => [String(id), index]));
  // Shopify ranks products; SKUs of the same product retain their editor order.
  return [...variants].sort((a, b) => (rank.get(a.productId) ?? Infinity) - (rank.get(b.productId) ?? Infinity));
}

export function groupStyles(rankedVariants, styleOrder = []) {
  const groups = new Map();
  for (const variant of rankedVariants) {
    const label = variant.style?.trim() || 'Other designs';
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(variant);
  }
  const priority = new Map(styleOrder.map((label, index) => [label.trim().toLowerCase(), index]));
  return new Map([...groups].sort(([a], [b]) => (priority.get(a.toLowerCase()) ?? Infinity) - (priority.get(b.toLowerCase()) ?? Infinity)));
}

export function orderLimitedStyles(rankedVariants, styleOrder = [], featuredProductIds = []) {
  const grouped = [...groupStyles(rankedVariants, styleOrder).values()].flat();
  // Feature existing, eligible designs in the merchant's order, then retain the grouped remainder.
  return rankVariants(grouped, featuredProductIds);
}

export function groupCoreStyles(rankedVariants) {
  const priorities = ['stripes', 'basics', 'checkered', 'pinstripes'];
  const group = variant => {
    const style = (variant.style || '').toLowerCase().replace(/[\s-]/g, '');
    if (priorities.includes(style)) return priorities.indexOf(style);
    if (variant.type === 'stripes' || variant.type === 'dual_stripes') return 0;
    if (variant.type === 'solid') return 1;
    if (variant.type === 'checkered') return 2;
    if (variant.type === 'vertical_stripes') return 3;
    return 4;
  };
  // Stable sorting retains bestseller order inside each style family.
  return [...rankedVariants].sort((a, b) => group(a) - group(b));
}

export function designTileCrop(zoom = 100, x = 50, y = 50) {
  const scale = Math.max(1, Math.min(4, Number(zoom) / 100 || 1));
  const edge = 50 / scale;
  const center = value => Math.max(edge, Math.min(100 - edge, Number.isFinite(Number(value)) ? Number(value) : 50));
  return { scale, x: center(x), y: center(y) };
}

export function isVariantVisible(variant, visibility) {
  if (!variant.available) return visibility.soldOut;
  return variant.preorder ? visibility.preorder : visibility.inStock;
}

export function bundleTotals(selection, variants, tiers) {
  let count = 0, subtotal = 0;
  for (const [id, quantity] of selection) {
    const variant = variants.get(id);
    if (!variant || !Number.isSafeInteger(quantity) || quantity < 1) continue;
    count += quantity;
    subtotal += variant.price * quantity;
  }
  return { count, subtotal, ...tierPricing(subtotal, count, tiers) };
}

export function tierPricing(amount, quantity, tiers) {
  const ordered = [...tiers].filter(tier => tier.quantity > 0).sort((a, b) => a.quantity - b.quantity);
  const unlocked = ordered.filter(tier => quantity >= tier.quantity).at(-1);
  const discount = Math.max(0, Math.min(100, Number(unlocked?.discount) || 0));
  return { discount, total: Math.round(amount * (100 - discount) / 100), nextTier: ordered.find(tier => quantity < tier.quantity) || null };
}

export function addonPricing(totals, { activeMember = false, selected = false, type, price = 0, discount = 10 } = {}) {
  const memberPrice = activeMember || (selected && type === 'membership');
  const percent = Math.max(0, Math.min(100, Number(discount) || 10));
  const addonPrice = selected ? Math.max(0, Number(price) || 0) : 0;
  // The extra member discount follows the quantity discount; add-ons do not earn either tier.
  return { ...totals, memberPrice, subtotal: totals.subtotal + addonPrice, total: (memberPrice ? Math.round(totals.total * (100 - percent) / 100) : totals.total) + addonPrice };
}

export function appendAddonItem(items, addon, requestId) {
  if (!addon?.selected || addon.type === 'membership') return items;
  if (!addon.available || !Number.isSafeInteger(Number(addon.id)) || Number(addon.id) <= 0) throw new Error('The selected add-on is no longer available.');
  return [...items, { id: Number(addon.id), quantity: 1, properties: { _BundleRequest: requestId } }];
}

// Use the same Subscribfy control as bundle-new, never a second subscription POST.
export async function selectMembershipWidget(getControl, wait = ms => new Promise(resolve => setTimeout(resolve, ms))) {
  await wait(1000);
  for (let attempt = 0; attempt < 5; attempt++) {
    const control = getControl();
    if (control) {
      if (control.classList.contains('exm_active')) return 'already-selected';
      control.click();
      return 'selected';
    }
    if (attempt < 4) await wait(500);
  }
  return 'unavailable';
}

export async function ensureMembershipCart(getControl, getCart, variantId, wait = ms => new Promise(resolve => setTimeout(resolve, ms))) {
  const hasMembership = cart => cart.items?.some(item => String(item.variant_id || item.id) === String(variantId) && item.quantity > 0 && item.selling_plan_allocation);
  // The cart is authoritative even when the third-party widget is still repainting.
  if (hasMembership(await getCart())) return 'already-selected';
  if (await selectMembershipWidget(getControl, wait) === 'unavailable') return 'unavailable';
  for (let check = 0; check < 6; check++) {
    await wait(300);
    if (hasMembership(await getCart())) return 'selected';
  }
  return 'unavailable';
}

export function requiresEarlyAccess(variants, activeMember, pageEarlyAccess = false) {
  if (activeMember) return false;
  return pageEarlyAccess || variants.some(variant => {
    let tags = variant?.productTags || [];
    if (typeof tags === 'string') {
      try { tags = JSON.parse(tags); } catch { tags = tags.split(','); }
    }
    return Array.isArray(tags) && tags.some(tag => String(tag).trim().toLowerCase().replace(/\s+/g, '-') === 'early-access');
  });
}

export function dispatchLabel(value, locale = 'en-GB') {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return 'Date to be confirmed';
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? 'Date to be confirmed' : date.toLocaleDateString(locale, { day:'numeric', month:'long', year:'numeric' });
}

export function shippingEstimate(selectedVariants, now = new Date(), businessDays = 2) {
  const preorder = selectedVariants.filter(variant => variant.preorder);
  const date = new Date(now);
  date.setHours(12, 0, 0, 0);
  let remaining = Number.isInteger(businessDays) && businessDays >= 1 && businessDays <= 7 ? businessDays : 2;
  while (remaining) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() !== 0 && date.getDay() !== 6) remaining--;
  }
  if (preorder.length) {
    for (const variant of preorder) {
      const value = variant.dispatch || '';
      const dispatch = new Date(`${value}T12:00:00`);
      const parts = value.split('-').map(Number);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(dispatch.getTime()) || dispatch.getFullYear() !== parts[0] || dispatch.getMonth() + 1 !== parts[1] || dispatch.getDate() !== parts[2]) {
        return { preorder: true, label: 'Ships by', date: 'To be confirmed' };
      }
      if (dispatch > date) date.setTime(dispatch.getTime());
    }
  }
  const day = date.getDate(), lastTwo = day % 100;
  const suffix = lastTwo >= 11 && lastTwo <= 13 ? 'th' : ({1:'st',2:'nd',3:'rd'}[day % 10] || 'th');
  const weekday = date.toLocaleDateString('en-US', {weekday:'short'});
  const month = date.toLocaleDateString('en-US', {month:'long'});
  return { preorder: !!preorder.length, label: 'Ships by', date: `${weekday}, ${month} ${day}${suffix}` };
}

export function buildCartItems(selection, variants, requestId) {
  if (!selection.size) throw new Error('Please add some pairs to your bundle.');
  return Array.from(selection, ([id, quantity]) => {
    const variant = variants.get(id);
    if (!variant?.available || !Number.isSafeInteger(Number(id)) || Number(id) <= 0 || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Please choose available pairs and a whole-number quantity.');
    const properties = { _BundleRequest: requestId };
    if (variant.preorder) {
      properties['_Pre-Order'] = 'Pre-order · Ships separately';
      properties['Order type'] = 'Pre-order · Ships separately';
      properties['Estimated dispatch'] = dispatchLabel(variant.dispatch);
    }
    return { id: Number(id), quantity, properties };
  });
}

export function pendingCartStatus(cart, attempt) {
  const quantities = new Map(), expected = new Map();
  for (const item of attempt.items) expected.set(String(item.id), (expected.get(String(item.id)) || 0) + item.quantity);
  for (const item of cart.items || []) {
    if (item.properties?._BundleRequest !== attempt.id) continue;
    const id = String(item.variant_id || item.variantId || item.id);
    quantities.set(id, (quantities.get(id) || 0) + item.quantity);
  }
  if (expected.size && quantities.size === expected.size && [...expected].every(([id, quantity]) => quantities.get(id) === quantity)) return 'complete';
  return quantities.size ? 'partial' : 'absent';
}

// Reuse UpCart's own add-to-cart update; otherwise refresh while its drawer opens.
// Drawer failures must never retry an add that Shopify has already accepted.
export async function refreshAndOpenUpCart(api, attempt, syncedRequest) {
  if (typeof api.upcartRefreshCart !== 'function' || typeof api.upcartOpenCart !== 'function') return false;
  let synced = Boolean(attempt && syncedRequest === attempt.id);
  if (!synced && attempt && typeof api.upcartGetCart === 'function') {
    try { synced = pendingCartStatus(api.upcartGetCart() || {}, attempt) === 'complete'; } catch { /* Refresh if the cached cart cannot be read. */ }
  }
  // Calling refresh first sets UpCart's loading state, but must not delay opening.
  const refresh = synced ? Promise.resolve() : Promise.resolve().then(() => api.upcartRefreshCart());
  const open = Promise.resolve().then(() => api.upcartOpenCart());
  await Promise.all([refresh, open]);
  return true;
}

export function remainingTime(deadline, now = Date.now()) {
  if (!deadline || !/([zZ]|[+-]\d{2}:\d{2})$/.test(deadline)) return null;
  const end = Date.parse(deadline);
  if (!Number.isFinite(end)) return null;
  return Math.max(0, Math.ceil((end - now) / 1000));
}

export function urgencyState({ mode, deadline, duration = 6147, sold = 88 }, startedAt, now = Date.now()) {
  const base = Math.max(0, Math.min(100, Number(sold) || 0));
  if (mode !== 'bundle-new') return { sold: base, remaining: mode === 'none' ? null : remainingTime(deadline, now) };
  const seconds = Number.isSafeInteger(Number(duration)) && Number(duration) > 0 ? Number(duration) : 6147;
  const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
  return {
    sold: Math.min(100, base + (elapsed >= 50 ? 3 : elapsed >= 20 ? 1 : 0)),
    remaining: seconds - elapsed % (seconds + 1)
  };
}

const DESIGN_ZOOM_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5M7.5 10.5h6M10.5 7.5v6"/></svg>';

if (typeof window !== 'undefined' && !customElements.get('elliosa-bundle-product-2')) {
  class BundleProduct extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.selection = new Map();
      this.pairs = new Map();
      this.viewId = null;
      this.productRankings = new Map();
      this.quantity = 4;
      this.activePair = null;
      this.pairPage = 0;
      this.customQuantity = false;
      this.quantityValid = true;
      this.busy = false;
      this.navigating = false;
      this.pending = null;
      this.selectionRestored = false;
      this.productSelectionInitialized = false;
      this.shoppingAlternatives = false;
      this.storageKey = `elliosa-bundle-product-2:${location.host}:${this.dataset.catalog}`;
      try { this.pending = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null'); } catch { /* Storage is optional. */ }
      this.addEventListener('click', event => this.onClick(event), { signal: this.abort.signal });
      window.matchMedia('(max-width: 767px)').addEventListener('change', () => {
        this.closeDesignPreview();
        this.syncHeroGallery();
      }, {signal:this.abort.signal});
      this.addEventListener('change', event => {
        if (event.target.matches('[data-bp-addon-toggle]')) {
          if (this.busy || this.pending) { event.target.checked = this.addon.selected; return; }
          this.addon.selected = event.target.checked;
          this.saveSelection();
          this.setAddonReveal(this.addon.type === 'membership' && this.addon.selected);
          this.renderSelection();
          return;
        }
      }, { signal: this.abort.signal });
      window.addEventListener('pageshow', () => {
        if (this.navigating) { this.navigating = false; this.busy = false; this.renderSelection(); }
      }, { signal: this.abort.signal });
      document.addEventListener('shopify:section:load', () => { this.loadCatalog(); this.applyResponsiveLayout?.(); }, { signal: this.abort.signal });
      document.addEventListener('shopify:section:unload', () => setTimeout(() => this.isConnected && this.loadCatalog(), 0), { signal: this.abort.signal });
      document.addEventListener('shopify:block:select', event => this.onEditorSelect(event), { signal: this.abort.signal });
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true, signal: this.abort.signal });
      else this.initialize();
    }

    disconnectedCallback() {
      this.closeDesignPreview(false); this.designDialog?.remove(); this.designDialog = null;
      this.closeDropdown(false, false); this.abort?.abort(); this.galleryAbort?.abort(); this.destroyGallery(); this.stickyObserver?.disconnect(); clearInterval(this.countdownInterval);
      this.designResizeObserver?.disconnect();
      this.stopWatchingUpCart?.();

    }

    initialize() {
      this.initializeResponsiveLayout();
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: this.dataset.currency || 'GBP' });
      const offers = JSON.parse(this.querySelector('[data-bp-tier-config]').textContent);
      this.quantity = initialOfferQuantity(offers);
      this.defaultQuantity = this.quantity;
      this.tiers = offers.sort((a,b) => a.quantity-b.quantity);
      this.initialGallery = Array.from(this.querySelectorAll('[data-bp-initial-image]')).map(node => ({ src: node.dataset.src, alt: node.dataset.alt, ratio: Number(node.dataset.ratio) || 1, blockId: node.dataset.blockId }));
      this.showingOpeningGallery = true;
      const addon = this.querySelector('[data-bp-addon-card]');
      this.addon = { activeMember: this.dataset.activeMember === 'true', discount: Number(this.dataset.membershipDiscount) || 10, selected: false, type: addon?.dataset.addonType, id: addon?.dataset.variantId, price: Number(addon?.dataset.price) || 0, available: addon?.dataset.available === 'true' };
      this.renderQuantityOptions();
      this.loadCatalog();
      this.setGallery(this.initialGallery);
      this.initializeStickyCart();
      // Warm both remote orders before the shopper reaches the style picker.
      OFFER_SORTS.forEach(({id}) => this.loadRanking(id));
      if (this.pending) this.showPending();
      this.urgencyStartedAt = Date.now();
      this.updateCountdown();
      if (this.querySelector('[data-bp-countdown-wrap]') && (this.dataset.urgencyMode === 'bundle-new' || (this.dataset.urgencyMode === 'deadline' && remainingTime(this.dataset.deadline) > 0))) {
        this.countdownInterval = setInterval(() => this.updateCountdown(), 1000);
      }
    }

    initializeResponsiveLayout() {
      const layout = this.querySelector('[data-bp-mobile-order]');
      if (!layout) return;
      const info = layout.querySelector('.bp-info');
      const areas = new Map(Array.from(layout.querySelectorAll('[data-bp-layout-area]'), node => [node.dataset.bpLayoutArea, node]));
      const mobile = window.matchMedia('(max-width: 767px)');
      const applyOrder = () => {
        const focused = this.contains(document.activeElement) ? document.activeElement : null;
        const order = (mobile.matches ? layout.dataset.bpMobileOrder : layout.dataset.bpDesktopOrder).split(',');
        if (!mobile.matches) layout.prepend(areas.get('gallery'));
        // Move existing nodes so gallery position, chosen pairs and event handlers survive a resize.
        order.forEach(area => {
          const node = areas.get(area);
          if (node && (mobile.matches || area !== 'gallery')) info.append(node);
        });
        if (focused && document.activeElement !== focused) focused.focus({ preventScroll: true });
        this.galleryNeedsResize = true;
        this.resizeGallery();
      };
      mobile.addEventListener('change', applyOrder, { signal: this.abort.signal });
      this.applyResponsiveLayout = applyOrder;
      applyOrder();
    }

    loadCatalog() {
      if (!this.money) return;
      const variants = new Map();
      [...document.querySelectorAll('[data-bp-style]')].sort((a,b) => Number(a.dataset.bpPageFallback !== undefined) - Number(b.dataset.bpPageFallback !== undefined)).forEach(source => {
        if (source.dataset.catalog !== this.dataset.catalog) return;
        source.querySelectorAll('[data-bp-option]').forEach(node => {
          const data = node.dataset;
          if (!data.variantId || variants.has(data.variantId)) return;
          const variant = {
            id: data.variantId, productId: data.productId, handle: data.productHandle, sku: data.sku, title: data.title, label: data.label, style: source.dataset.styleLabel,
            productTags: data.productTags, filterColors: data.filterColors,
            price: Number(data.price), available: data.available === 'true', preorder: data.preorder === 'true',
            dispatch: data.dispatch, category: data.category || source.dataset.category || 'limited', blockId: data.blockId,
            image: data.image, type: data.swatchType, color: data.color, secondary: data.secondary, accent: data.accent, swatch: data.swatchImage, tileImage: data.tileImage, tileZoom: Number(data.tileZoom) || 100, tileX: Number(data.tileX ?? 50), tileY: Number(data.tileY ?? 50),
            gallery: Array.from(node.querySelectorAll('[data-gallery-image]')).map(image => ({ src: image.dataset.src, alt: image.dataset.alt, ratio: Number(image.dataset.ratio) || 1 }))
          };
          variant.filters = offerFilterData(variant);
          if (Number.isFinite(variant.price) && isVariantVisible(variant, {
            inStock: this.dataset.showInStock !== 'false',
            preorder: this.dataset.showPreorder !== 'false',
            soldOut: this.dataset.showSoldOut === 'true'
          })) variants.set(variant.id, variant);
        });
      });
      this.variants = variants;
      if (!this.productSelectionInitialized && variants.size) {
        this.productSelectionInitialized = true;
        // Seed dormant slots too, so larger offers start with the visited product.
        // Restore a shopper's exact saved choices afterwards, including cleared slots.
        if (!this.pairs.size && !this.dataset.pageAvailability && !this.shoppingAlternatives) this.pairs = initialProductPairs(this.tiers, variants, this.dataset.pageProductId, this.dataset.pageVariantId);
      }
      this.restoreSelection();
      this.pairs = new Map([...this.pairs].filter(([, id]) => variants.get(id)?.available));
      this.viewId = variants.has(this.viewId) ? this.viewId : null;
      this.renderAvailability();
      // Keep the opening hero gallery while initializing or restoring pair choices.
      this.renderGroups(); this.renderSelection();
      if (!variants.size) this.message('No styles are available in this bundle right now.', true);
    }

    get earlyAccessLocked() {
      const selection = pairSelection(this.pairs, this.quantity, this.variants || new Map());
      return requiresEarlyAccess([...selection.keys()].map(id => this.variants.get(id)), this.dataset.activeMember === 'true', this.dataset.pageEarlyAccess === 'true');
    }

    openEarlyAccess(trigger = this.querySelector('[data-bp-add]')) {
      this.saveSelection();
      document.dispatchEvent(new CustomEvent('elliosa:early-access', {detail: {trigger, returnTo: window.location.pathname + window.location.search}}));
    }

    get availabilityBlocked() { return Boolean(this.dataset.pageAvailability && !this.shoppingAlternatives); }

    renderAvailability() {
      const notice = this.querySelector('[data-bp-availability-notice]');
      if (!this.dataset.pageAvailability || !notice) return;
      this.dataset.pendingCart = String(Boolean(this.pending));
      // With no purchasable alternatives, never reveal an empty or zero-priced builder.
      const available = [...(this.variants?.values() || [])].some(variant => variant.available);
      this.dataset.shoppingAlternatives = String(Boolean(this.shoppingAlternatives && available));
      if (!available) this.shoppingAlternatives = false;
      notice.hidden = !this.availabilityBlocked;
      const button = this.querySelector('[data-bp-shop-designs]');
      button.hidden = !available;
      button.disabled = !available || this.busy || Boolean(this.pending);
      const copy = this.querySelector('[data-bp-availability-copy]');
      copy.textContent = !available ? 'No designs are available to shop right now. Please check back soon.'
        : this.dataset.pageAvailability === 'coming-soon' ? 'Explore our available designs while you wait.'
        : 'Explore our available styles and find your next pair.';
    }

    startAlternativeShopping() {
      if (!this.availabilityBlocked || this.busy || this.pending || ![...this.variants.values()].some(variant => variant.available)) return;
      this.shoppingAlternatives = true;
      this.quantity = this.defaultQuantity ?? initialOfferQuantity(this.tiers);
      this.pairs = new Map();
      this.productSelectionInitialized = true;
      this.selectionRestored = true;
      this.addon.selected = false;
      const toggle = this.querySelector('[data-bp-addon-toggle]');
      if (toggle) toggle.checked = false;
      this.setAddonReveal(false);
      this.viewId = null;
      this.syncHeroGallery();
      this.message('');
      this.saveSelection();
      this.renderAvailability();
      this.renderSelection();
      const first = this.querySelector(`[data-bp2-tier="${this.quantity}"] .ptrig`);
      first?.focus({preventScroll:true});
      first?.scrollIntoView({block:'nearest',behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }

    async loadRanking(sort) {
      if (!this.dataset.rankUrl || ['loading','ready'].includes(this.productRankings.get(sort)?.status)) return;
      this.productRankings.set(sort, {status:'loading'});
      try {
        const url = new URL(this.dataset.rankUrl, location.origin);
        url.searchParams.set('sort_by', sort);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
        const ids = [], taggedIds = [], visited = new Set();
        let priorityIds = [];
        let page = 1;
        do {
          if (visited.has(page)) throw new Error('Repeated ranking page');
          visited.add(page); url.searchParams.set('page', String(page));
          const response = await fetch(url, { signal:this.abort.signal });
          if (!response.ok) throw new Error('Ranking unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const data = JSON.parse(html.querySelector('[data-bp-ranking]')?.textContent || 'null');
          if (data?.sort !== sort || !Array.isArray(data.products)) throw new Error('Invalid ranking');
          ids.push(...data.products.map(String));
          if (sort === 'best-selling') {
            if (!Array.isArray(data.priority) || !Array.isArray(data.tagged)) throw new Error('Invalid curated ranking');
            priorityIds = data.priority.map(String);
            taggedIds.push(...data.tagged.map(String));
          }
          page = Number(data.next) || 0;
        } while (page);
        if (!this.isConnected) return;
        this.productRankings.set(sort, {status:'ready',ids:sort === 'best-selling' ? priorityIds : ids,taggedIds});
        if (sort === 'best-selling') this.bestSellerIds = priorityIds;
        // A background ranking must not replace buttons in a different active filter.
        if (this.dropdownFilter === sort) this.renderGroups();
      } catch {
        // Category browsing remains available; never present editor order as a verified ranking.
        this.productRankings.set(sort, {status:'error'});
        if (this.isConnected && this.dropdownFilter === sort) this.renderGroups();
      }
    }

    onClick(event) {
      if (event.target.closest('[data-bp-add], [data-bp-sticky-add]')) event.stopPropagation();
      const preview = event.target.closest('[data-bp2-preview]');
      if (preview) { this.openDesignPreview(preview.dataset.bp2Preview, preview); return; }
      if (this.designDialog?.contains(event.target)) return;
      if (this.handleOfferClick(event)) return;
      if (event.target.closest('[data-bp-review-link]') && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        const reviews = document.getElementById('looxReviews');
        if (reviews) {
          event.preventDefault();
          reviews.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        }
        return;
      }
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || this.busy) return;
      if (button.matches('[data-bp-image]')) this.showImage(Number(button.dataset.bpImage));
      else if (button.matches('[data-bp-image-prev]')) this.showImage(this.imageIndex - 1);
      else if (button.matches('[data-bp-image-next]')) this.showImage(this.imageIndex + 1);
      else if (button.matches('[data-bp-addon-explain]')) this.setAddonReveal(button.getAttribute('aria-expanded') !== 'true');
      else if (button.matches('[data-bp-check-cart]')) this.checkPending();
      else if (button.matches('[data-bp-add], [data-bp-sticky-add]')) {
        if (this.earlyAccessLocked) this.openEarlyAccess(button);
        else if (this.ready) this.addToCart();
        else this.focusSelection();
      }
    }

    setAddonReveal(open) {
      const reveal = this.querySelector('[data-bp-addon-reveal]');
      if (!reveal) return;
      reveal.classList.toggle('is-open', open);
      reveal.setAttribute('aria-hidden', String(!open));
      reveal.inert = !open;
      this.querySelector('[data-bp-addon-explain]')?.setAttribute('aria-expanded', String(open));
    }

    get giftsEnabled() { return this.dataset.giftOffersEnabled === 'true'; }

    get giftBenefits() { return this.giftsEnabled ? bundleGiftBenefits(this.gifts, this.dataset.freeShippingUnlock) : []; }

    renderQuantityOptions() {
      this.gifts = this.giftsEnabled ? JSON.parse(this.querySelector('[data-bp-gift-config]').textContent).slice(0, 1) : [];
      const holder = this.querySelector('[data-bp2-tiers]');
      holder.innerHTML = this.tiers.map(tier => {
        const n = tier.quantity;
        const giftCount = this.giftBenefits.filter(gift => n >= gift.unlock).length;
        const caption = offerCaption(tier, giftCount);
        const badgeText = tier.badgeText?.trim() || (n === 4 ? 'Most popular' : n === 8 ? 'Best value' : '');
        const badgeIcon = tier.badgeIcon && tier.badgeIcon !== 'auto' ? tier.badgeIcon : n === 8 ? 'seal' : 'star';
        return `<div class="tier" data-bp2-tier="${n}" ${tier.editorAttributes || ''}>
          ${tier.showBadge !== false && badgeText ? `<span class="badge" style="--bp2-badge-bg:${offerEscape(tier.badgeBackground || '#f8fec1')};--bp2-badge-text:${offerEscape(tier.badgeColor || '#1c1c1c')};--bp2-badge-border:${offerEscape(tier.badgeBorder || '#1c1c1c')}">${OFFER_ICONS[badgeIcon] || ''}${offerEscape(badgeText)}</span>` : ''}
          <button class="tier-head" type="button" data-bp2-size="${n}" aria-expanded="false" aria-controls="bp2-body-${this.dataset.catalog}-${n}">
            <span class="radio" aria-hidden="true"></span><span class="t-copy"><span class="t-name">${n} ${n === 1 ? 'Pair' : 'Pairs'}</span><span class="pp"></span>${caption ? `<span class="t-sub">${offerEscape(caption)}</span>` : ''}</span>
            <span class="t-price"><span class="now"></span><span class="was"></span></span>
          </button>
          <div class="tier-body" id="bp2-body-${this.dataset.catalog}-${n}" inert><div class="tier-inner"><div class="rows">
            <div class="hint">Choose a style for each pair</div>
            ${Array.from({length:n}, (_,index) => `<div class="prow" data-bp2-row="${index}"><span class="ptile">PAIR<b>${index + 1}</b></span><div class="bp2-pair-choice"><button class="ptrig" data-bp2-pick="${index}" type="button" aria-expanded="false" aria-haspopup="dialog"></button><button type="button" class="bp2-view-design" data-bp2-preview hidden></button></div></div>`).join('')}
          </div></div></div>
        </div>`;
      }).join('');
      this.addEventListener('keydown', event => {
        if (this.designDialog?.open) return;
        if (event.key === 'Escape') { event.preventDefault(); if (this.designPreview?.inline) this.backToPicker(); else if (this.advancedFiltersOpen && this.openRow) this.showAdvancedFilters(false); else this.closeDropdown(true); return; }
        if (!this.openRow) return;
        if (this.designPreview?.inline) {
          if (!this.designPreview.zoomed && ['ArrowLeft','ArrowRight'].includes(event.key)) {event.preventDefault(); this.showDesignPhoto(this.designPreview.index + (event.key === 'ArrowRight' ? 1 : -1));}
          return;
        }
        if (this.advancedFiltersOpen) return;
        const picker = this.pickerDialog || this.openRow;
        if (event.key === 'Tab') {
          if (this.pickerDialog?.open) return; // Native dialog traps focus without dismissing the picker.
          const row = this.openRow;
          // Wait for keyboard navigation to finish. Safari can focus the surrounding
          // tabindex=-1 section before dispatching a pointer click on an option;
          // closing on every focusin would remove that option before it can select.
          setTimeout(() => {
            if (!this.designDialog?.open && this.openRow === row && !row.contains(document.activeElement)) this.closeDropdown();
          }, 0);
          return;
        }
        const options = [...picker.querySelectorAll('.dd-opt:not(:disabled), .bp2-option-remove:not(:disabled)')];
        const position = options.indexOf(document.activeElement);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault(); options[(position + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length]?.focus();
        }
      }, {signal:this.abort.signal});
      document.addEventListener('click', event => {
        if (!this.designDialog?.open && this.openRow && !event.composedPath().includes(this.openRow) && !event.composedPath().includes(this.pickerDialog)) this.closeDropdown();
      }, {signal:this.abort.signal});
    }

    handleOfferClick(event) {
      const button = event.target.closest('button');
      if (!button || this.busy || this.pending) return false;
      if (button.matches('[data-bp-shop-designs]')) { this.startAlternativeShopping(); return true; }
      if (this.availabilityBlocked && button.matches('[data-bp2-size], [data-bp2-pick]')) return true;
      if (this.pickerConfirmation) return true;
      if (button.matches('[data-bp2-faq-toggle]')) {
        const open = button.getAttribute('aria-expanded') !== 'true';
        this.querySelectorAll('[data-bp2-faq-toggle]').forEach(toggle => {
          const active = toggle === button && open;
          toggle.setAttribute('aria-expanded', String(active));
          toggle.nextElementSibling.hidden = !active;
          toggle.nextElementSibling.classList.toggle('active', active);
        });
        return true;
      }
      if (button.matches('[data-bp2-size]')) {
        this.closeDropdown(); this.quantity = Number(button.dataset.bp2Size);
        this.saveSelection();
        this.message(''); this.renderSelection(); return true;
      }
      if (button.matches('[data-bp2-pick]')) {
        const row = button.closest('[data-bp2-row]');
        if (this.openRow === row) this.closeDropdown(); else this.openDropdown(row);
        return true;
      }
      if (button.matches('[data-bp2-picker-preview]')) {
        if (this.openRow && this.pickerDialog?.contains(button)) this.openPickerPreview(button.dataset.bp2PickerPreview,button);
        return true;
      }
      if (button.matches('[data-bp2-gallery-back]')) {this.backToPicker(); return true;}
      if (this.designPreview?.inline && this.pickerDialog?.contains(button)) {
        const state = this.designPreview;
        if (button.matches('[data-design-prev]')) this.showDesignPhoto(state.index - 1);
        else if (button.matches('[data-design-next]')) this.showDesignPhoto(state.index + 1);
        else if (button.matches('[data-design-dot]')) this.showDesignPhoto(Number(button.dataset.designDot));
        else if (button.matches('[data-design-zoom], [data-design-photo]')) this.toggleDesignZoom();
        else if (button.matches('[data-design-choose]')) this.selectDesign(state.pair,state.id,true);
        return true;
      }
      if (button.matches('[data-bp2-advanced-open], [data-bp2-advanced-back], [data-bp2-advanced-apply], [data-bp2-facet], [data-bp2-filter-clear]')) {
        if (!this.openRow || this.dataset.advancedFilters !== 'true' || !this.pickerDialog?.contains(button)) return true;
        if (button.matches('[data-bp2-advanced-open]')) this.showAdvancedFilters(true);
        else if (button.matches('[data-bp2-advanced-back], [data-bp2-advanced-apply]')) this.showAdvancedFilters(false);
        else {
          if (button.matches('[data-bp2-filter-clear]')) this.advancedFilters = {};
          else {
            const group = button.dataset.bp2Facet, value = button.dataset.value;
            if (!this.pickerFilterOptions.some(option => option.id === group && (!value || option.options.some(([id]) => id === value)))) return true;
            const selected = this.advancedFilters[group] || [];
            this.advancedFilters[group] = !value ? [] : selected.includes(value) ? selected.filter(id => id !== value) : [...selected,value];
          }
          this.fillDropdown();
          this.pickerDialog.querySelector('.dd-list').scrollTop = 0;
          if (!this.advancedFilterCount() && button.matches('[data-bp2-filter-clear]') && !this.advancedFiltersOpen) this.pickerDialog.querySelector('[data-bp2-advanced-open]').focus({preventScroll:true});
        }
        return true;
      }
      if (button.matches('[data-bp2-filter]')) {
        if (!this.openRow) return true;
        this.dropdownFilter = button.dataset.bp2Filter;
        if (OFFER_SORTS.some(sort => sort.id === this.dropdownFilter)) this.loadRanking(this.dropdownFilter);
        this.fillDropdown();
        const picker = this.pickerDialog || this.openRow;
        picker.querySelector('.dd-list').scrollTop = 0;
        picker.querySelector(`[data-bp2-filter="${this.dropdownFilter}"]`)?.focus({preventScroll:true}); return true;
      }
      if (button.matches('[data-bp2-remove]')) {
        const row = this.pickerDialog?.contains(button) ? this.openRow : button.closest('[data-bp2-row]');
        if (row !== this.openRow || !row) return true;
        this.removeDesign(Number(row.dataset.bp2Row), button.dataset.bp2Remove);
        return true;
      }
      if (button.matches('[data-bp2-option]')) {
        const row = this.pickerDialog?.contains(button) ? this.openRow : button.closest('[data-bp2-row]');
        if (row !== this.openRow || !row) return true;
        this.selectDesign(Number(row.dataset.bp2Row), button.dataset.bp2Option, true);
        return true;
      }
      return false;
    }

    selectDesign(index, id, focus = false) {
      if (this.busy || this.pending || this.pickerConfirmation) return false;
      try { this.pairs = choosePair(this.pairs, index, this.variants.get(id), this.quantity); }
      catch (error) { this.message(error.message, true); return false; }
      this.saveSelection();
      this.message('');
      // Commit the visible pair and cart state before changing the optional photo gallery.
      this.renderSelection();
      if (this.pickerDialog?.open && this.openRow) this.confirmPickerSelection(id, focus);
      else this.closeDropdown(focus);
      this.viewVariant(id);
      return true;
    }

    confirmPickerSelection(id, focus) {
      const dialog = this.pickerDialog;
      const confirmation = {dialog, id};
      this.pickerConfirmation = confirmation;
      dialog.inert = true;
      if (this.designPreview?.inline) {
        const action = dialog.querySelector('[data-design-choose]');
        action.innerHTML = `${OFFER_ICONS.check}<span>Selected</span>`;
      } else {
        const list = dialog.querySelector('.dd-list'), scrollTop = list.scrollTop;
        this.fillDropdown();
        list.scrollTop = scrollTop;
      }
      // Keep the confirmation visible before starting the existing smooth dismissal.
      this.pickerConfirmationTimer = setTimeout(() => {
        if (this.pickerConfirmation === confirmation && this.pickerDialog === dialog) this.closeDropdown(focus);
      }, 350);
    }

    removeDesign(index, id) {
      if (this.busy || this.pending || !Number.isSafeInteger(index) || index < 0 || index >= this.quantity || this.pairs.get(index) !== id) return false;
      const picker = this.pickerDialog || this.openRow;
      const list = picker?.querySelector('.dd-list');
      const scrollTop = list?.scrollTop || 0;
      this.pairs = new Map(this.pairs);
      this.pairs.delete(index);
      this.saveSelection();
      this.message(''); this.renderSelection();
      this.fillDropdown();
      if (list) list.scrollTop = scrollTop;
      picker?.querySelector(`[data-bp2-option="${id}"]`)?.focus({preventScroll:true});
      return true;
    }

    lockPageScroll() {
      const state = {scrollX:window.scrollX, scrollY:window.scrollY};
      // The theme's sticky header otherwise interprets fixed-body locking as a scroll to the top.
      state.stopThemeScroll = event => event.stopImmediatePropagation();
      document.addEventListener('theme:scroll',state.stopThemeScroll,true);
      const lock = (element, values) => Object.entries(values).map(([name,value]) => {
        const saved = [element,name,element.style.getPropertyValue(name),element.style.getPropertyPriority(name)];
        element.style.setProperty(name,value); return saved;
      });
      state.savedStyles = [
        ...lock(document.documentElement, {overflow:'hidden'}),
        ...lock(document.body, {position:'fixed',top:`-${state.scrollY}px`,left:`-${state.scrollX}px`,width:'100%',overflow:'hidden'})
      ];
      return state;
    }

    restorePageScroll(state) {
      if (!state) return;
      for (const [element,name,value,priority] of state.savedStyles || []) {
        if (value) element.style.setProperty(name,value,priority); else element.style.removeProperty(name);
      }
      window.scrollTo({left:state.scrollX,top:state.scrollY,behavior:'instant'});
      const release = () => document.removeEventListener('theme:scroll',state.stopThemeScroll,true);
      if (window.requestAnimationFrame) window.requestAnimationFrame(() => window.requestAnimationFrame(release)); else release();
    }

    openDropdown(row) {
      this.closeDropdown(false, false);
      if (!row || this.busy || this.pending) return;
      this.openRow = row; this.dropdownFilter = 'all';
      this.advancedFiltersOpen = false;
      this.advancedFilters = this.dataset.advancedFilters === 'true' ? this.advancedFilters || {} : {};
      this.pickerFilterOptions = offerFilterOptions(this.variants.values());
      const trigger = row.querySelector('.ptrig');
      row.classList.add('open'); trigger.setAttribute('aria-expanded','true');
      const dialog = document.createElement('dialog');
      const id = `bp2-picker-${this.dataset.catalog}`;
      dialog.id = id; dialog.className = 'bp2-picker-dialog';
      dialog.setAttribute('aria-labelledby', `${id}-title`);
      trigger.setAttribute('aria-controls', id);
      dialog.innerHTML = `<div class="bp2-picker-shell">
        <header class="bp2-picker-header"><button type="button" data-bp2-advanced-back aria-label="Back to styles" hidden><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 6-6 6 6 6"/></svg></button><div><h2 id="${offerEscape(id)}-title">Choose your style</h2></div><button type="button" data-bp2-picker-close aria-label="Close style picker" autofocus><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button></header>
        <div class="bp2-picker-toolbar"><div class="dd-filters" role="group" aria-label="Filter styles">${OFFER_FILTERS.map(group => `<button type="button" data-bp2-filter="${group.id}">${group.label}</button>`).join('')}</div>${this.dataset.advancedFilters === 'true' ? `<button type="button" data-bp2-advanced-open aria-label="Advanced filters" aria-expanded="false" aria-controls="${offerEscape(id)}-filters"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h8m4 0h4M4 17h2m4 0h10"/><circle cx="14" cy="7" r="2"/><circle cx="8" cy="17" r="2"/></svg><span data-bp2-filter-count hidden></span></button>` : ''}</div>
        ${this.dataset.advancedFilters === 'true' ? this.advancedFilterMarkup(id) : ''}
        <div class="bp2-filter-summary" hidden><span data-bp2-result-count role="status" aria-live="polite"></span><button type="button" data-bp2-filter-clear>Clear filters</button></div>
        <div class="dd-list" role="group" aria-label="Choose a style"></div>
        <div class="bp2-picker-gallery" hidden>${this.designStageMarkup()}<button type="button" class="bp2-design-choose" data-design-choose></button></div>
      </div>`;
      this.pickerDialog = dialog; this.append(dialog); this.fillDropdown();
      dialog.addEventListener('cancel', event => {event.preventDefault(); if (this.designPreview?.inline) this.backToPicker(); else if (this.advancedFiltersOpen) this.showAdvancedFilters(false); else this.closeDropdown(true);}, {signal:this.abort.signal});
      const track = dialog.querySelector('[data-design-track]');
      track.addEventListener('scroll', () => {
        const state = this.designPreview;
        if (!state?.inline || !track.clientWidth) return;
        state.index = Math.max(0,Math.min(state.images.length - 1,Math.round(track.scrollLeft / track.clientWidth)));
        this.updateDesignControls();
      }, {passive:true,signal:this.abort.signal});
      dialog.addEventListener('click', event => {
        if (event.target === dialog || event.target.closest('[data-bp2-picker-close]')) {
          event.stopPropagation(); this.closeDropdown(true);
        }
      }, {signal:this.abort.signal});
      this.pickerScrollState = this.lockPageScroll();
      dialog.showModal();
      dialog.querySelector('[data-bp2-picker-close]').focus({preventScroll:true});
      const selected = dialog.querySelector('.bp2-design-option.sel');
      if (selected) {
        const list = dialog.querySelector('.dd-list');
        list.scrollTop = Math.max(0, selected.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop - 5);
      }
    }

    openPickerPreview(id, opener) {
      const variant = this.variants.get(id), dialog = this.pickerDialog;
      if (!variant || !dialog?.open || !this.openRow) return;
      const images = (variant.gallery?.length ? variant.gallery : [{src:variant.image,alt:variant.title}]).filter(image => image.src);
      if (!images.length) return;
      this.designPreview = {id,pair:Number(this.openRow.dataset.bp2Row),opener,images,index:0,zoomed:false,inline:true};
      dialog.querySelector('.bp2-picker-toolbar').hidden = true;
      dialog.querySelector('.bp2-filter-summary').hidden = true;
      dialog.querySelector('.dd-list').hidden = true;
      dialog.querySelector('.bp2-picker-gallery').hidden = false;
      dialog.querySelector('[data-bp2-picker-close]').hidden = true;
      const back = dialog.querySelector('[data-bp2-advanced-back]');
      back.hidden = false; back.setAttribute('data-bp2-gallery-back',''); back.removeAttribute('data-bp2-advanced-back');
      dialog.querySelector('h2').textContent = variant.title;
      const action = dialog.querySelector('[data-design-choose]');
      action.textContent = 'Choose';
      action.disabled = !variant.available || this.busy || Boolean(this.pending);
      this.populateDesignPhotos(dialog,variant,images);
      dialog.querySelector('[data-design-track]').inert = false;
      dialog.querySelector('[data-design-track]').scrollLeft = 0;
      dialog.querySelector('[data-design-zoom]').setAttribute('aria-pressed','false');
      dialog.querySelector('[data-design-zoom]').setAttribute('aria-label','Zoom in');
      back.focus({preventScroll:true});
    }

    backToPicker() {
      const state = this.designPreview, dialog = this.pickerDialog;
      if (!state?.inline || !dialog) return;
      this.designPreview = null;
      dialog.querySelector('.bp2-picker-gallery').hidden = true;
      dialog.querySelector('[data-bp2-picker-close]').hidden = false;
      dialog.querySelector('.bp2-picker-toolbar').hidden = false;
      dialog.querySelector('.dd-list').hidden = false;
      const back = dialog.querySelector('[data-bp2-gallery-back]');
      back.hidden = true; back.removeAttribute('data-bp2-gallery-back'); back.setAttribute('data-bp2-advanced-back','');
      dialog.querySelector('h2').textContent = 'Choose your style';
      this.updateAdvancedFilters(this.pickerResultCount);
      dialog.querySelector(`[data-bp2-picker-preview="${state.id}"]`)?.focus({preventScroll:true});
    }

    advancedFilterMarkup(id) {
      return `<div class="bp2-advanced-panel" id="${offerEscape(id)}-filters" hidden><div class="bp2-advanced-groups">${this.pickerFilterOptions.map(group => `<fieldset><legend>${group.label}</legend><div class="bp2-facet-options"><button type="button" data-bp2-facet="${group.id}" data-value="">All</button>${group.options.map(([value,label]) => `<button type="button" data-bp2-facet="${group.id}" data-value="${offerEscape(value)}">${group.id === 'colors' ? `<span class="bp2-color-dot" style="--filter-color:${FILTER_COLORS[value] || '#d9d5cf'}" aria-hidden="true"></span>` : ''}${offerEscape(label)}</button>`).join('')}</div></fieldset>`).join('')}</div><footer class="bp2-advanced-footer"><button type="button" data-bp2-filter-clear>Clear all</button><button type="button" data-bp2-advanced-apply>Show styles</button></footer></div>`;
    }

    advancedFilterCount() {
      return this.dataset.advancedFilters === 'true' ? Object.values(this.advancedFilters || {}).reduce((sum,values) => sum + values.length,0) : 0;
    }

    showAdvancedFilters(open) {
      const dialog = this.pickerDialog;
      if (!dialog || this.dataset.advancedFilters !== 'true') return;
      this.advancedFiltersOpen = open;
      dialog.querySelector('.bp2-advanced-panel').hidden = !open;
      dialog.querySelector('.bp2-picker-toolbar').hidden = open;
      dialog.querySelector('.dd-list').hidden = open;
      dialog.querySelector('[data-bp2-advanced-back]').hidden = !open;
      dialog.querySelector('h2').textContent = open ? 'Filters' : 'Choose your style';
      dialog.querySelector('[data-bp2-advanced-open]').setAttribute('aria-expanded',String(open));
      this.fillDropdown();
      if (!open) dialog.querySelector('.dd-list').scrollTop = 0;
      dialog.querySelector(open ? '[data-bp2-advanced-back]' : '[data-bp2-advanced-open]').focus({preventScroll:true});
    }

    updateAdvancedFilters(resultCount) {
      const dialog = this.pickerDialog;
      if (!dialog || this.dataset.advancedFilters !== 'true') return;
      const count = this.advancedFilterCount();
      const badge = dialog.querySelector('[data-bp2-filter-count]');
      badge.hidden = !count; badge.textContent = count;
      dialog.querySelector('[data-bp2-advanced-open]').setAttribute('aria-label',count ? `Advanced filters, ${count} selected` : 'Advanced filters');
      dialog.querySelector('.bp2-filter-summary').hidden = this.advancedFiltersOpen || this.designPreview?.inline || !count;
      dialog.querySelector('[data-bp2-result-count]').textContent = resultCount == null ? 'Loading styles…' : `${resultCount} ${resultCount === 1 ? 'style' : 'styles'}`;
      dialog.querySelector('[data-bp2-advanced-apply]').textContent = resultCount == null ? 'Show styles' : `Show ${resultCount} ${resultCount === 1 ? 'style' : 'styles'}`;
      dialog.querySelectorAll('[data-bp2-facet]').forEach(button => {
        const selected = this.advancedFilters[button.dataset.bp2Facet] || [];
        button.setAttribute('aria-pressed',String(button.dataset.value ? selected.includes(button.dataset.value) : !selected.length));
      });
    }

    closeDropdown(focus = false, animate = true) {
      clearTimeout(this.pickerConfirmationTimer); this.pickerConfirmation = null;
      // Finish any previous dismissal before another modal takes ownership of scroll locking.
      this.finishPickerClose?.();
      if (!this.openRow) return;
      if (this.designPreview?.inline) this.designPreview = null;
      const row = this.openRow, trigger = row.querySelector('.ptrig');
      const dialog = this.pickerDialog, scrollState = this.pickerScrollState;
      row.classList.remove('open'); trigger.setAttribute('aria-expanded','false');
      trigger.removeAttribute('aria-controls'); this.openRow = null;
      let finished = false;
      const onCloseAnimationEnd = event => {
        if (event.target === dialog && event.animationName === 'bp2SheetOut' && !event.pseudoElement) finish();
      };
      const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(this.pickerCloseTimer); this.finishPickerClose = null;
        dialog?.removeEventListener('animationend',onCloseAnimationEnd);
        dialog?.close(); dialog?.remove();
        this.pickerDialog = null; this.pickerScrollState = null;
        this.restorePageScroll(scrollState);
        if (focus && trigger.isConnected) trigger.focus({preventScroll:true});
      };
      if (!dialog?.open || !animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {finish(); return;}
      dialog.inert = true;
      dialog.addEventListener('animationend',onCloseAnimationEnd);
      dialog.classList.add('is-closing');
      this.finishPickerClose = finish;
      // Let the sheet and backdrop finish painting; a timer at the CSS duration can cut them short.
      this.pickerCloseTimer = setTimeout(finish, 450);
    }

    fillDropdown() {
      const row = this.openRow; if (!row) return;
      const selectedId = this.pairs.get(Number(row.dataset.bp2Row));
      const picker = this.pickerDialog || row;
      picker.querySelectorAll('[data-bp2-filter]').forEach(button => {
        const active = button.dataset.bp2Filter === this.dropdownFilter;
        button.classList.toggle('on', active); button.setAttribute('aria-pressed', String(active));
      });
      const list = picker.querySelector('.dd-list');
      const sort = OFFER_SORTS.find(sort => sort.id === this.dropdownFilter);
      const ranking = sort && this.productRankings.get(sort.id);
      list.setAttribute('aria-busy', String(Boolean(sort && (!ranking || ranking.status === 'loading'))));
      if (sort && ranking?.status !== 'ready') {
        this.updateAdvancedFilters(null);
        list.innerHTML = `<p class="dd-status" role="status">${ranking?.status === 'error' ? 'This order is unavailable. Choose All to browse styles.' : 'Loading styles…'}</p>`;
        return;
      }
      const ranked = rankVariants(this.variants.values(), this.bestSellerIds || []);
      const ordered = [...groupCoreStyles(ranked.filter(v => v.category === 'core')), ...orderLimitedStyles(ranked.filter(v => v.category !== 'core'), (this.dataset.limitedStyleOrder || '').split(','), (this.dataset.featuredLimitedProducts || '').split(','))];
      const groups = (sort ? [{...sort, options:sort.id === 'best-selling' ? bestSellerOfferOptions(this.variants.values(),ranking.ids,ranking.taggedIds) : rankedOfferOptions(this.variants.values(),ranking.ids)}] : offerOptionGroups(ordered, this.dropdownFilter, this.dataset.pageProductId, this.dataset.pageVariantId))
        .map(group => ({...group,options:group.options.filter(variant => matchesOfferFilters(variant,this.dataset.advancedFilters === 'true' ? this.advancedFilters : {}))}));
      this.pickerResultCount = groups.reduce((sum,group) => sum + group.options.length,0);
      this.updateAdvancedFilters(this.pickerResultCount);
      list.innerHTML = groups.map(group => {
        if (!group.options.length) return '';
        return `<div class="dd-option-group"><div class="dd-group">${group.label}</div>` + group.options.map(variant => {
          const selected = selectedId === variant.id;
          const confirming = selected && this.pickerConfirmation?.id === variant.id;
          const total = addonPricing({...tierPricing(variant.price,this.quantity,this.tiers),subtotal:variant.price},{...this.addon,price:0}).total;
          return `<div class="bp2-design-option ${selected ? 'sel' : ''}"><button type="button" class="dd-opt ${selected ? 'sel' : ''}" data-bp2-option="${variant.id}" aria-pressed="${selected}" ${variant.available ? '' : 'disabled'}>
            <img src="${offerEscape(variant.image)}" alt="" loading="lazy" width="112" height="112"><span class="bp2-option-copy"><span class="nm">${offerEscape(variant.title)}</span><span class="sb">One Size</span><span class="pr">${this.money.format(total/100)}${total < variant.price ? `<s>${this.money.format(variant.price/100)}</s>` : ''}</span>${variant.preorder ? `<span class="bp2-preorder">Pre-order · ${offerEscape(dispatchLabel(variant.dispatch))}</span>` : ''}${!variant.available ? '<span class="sb">Sold out</span>' : ''}</span>${selected ? '' : '<span class="bp2-option-radio" aria-hidden="true"></span>'}</button><button type="button" class="bp2-picker-preview" data-bp2-picker-preview="${variant.id}" aria-label="Preview ${offerEscape(variant.title)} photos"><span>${DESIGN_ZOOM_ICON}</span></button>${confirming ? `<span class="bp2-option-confirmed" aria-hidden="true">${OFFER_ICONS.check}</span>` : selected ? `<button type="button" class="bp2-option-remove" data-bp2-remove="${variant.id}" aria-label="Remove ${offerEscape(variant.title)} from pair ${Number(row.dataset.bp2Row) + 1}">${OFFER_ICONS.remove}</button>` : ''}</div>`;
        }).join('') + '</div>';
      }).join('') || `<p class="dd-status" role="status">${this.advancedFilterCount() ? 'No styles match these filters.' : 'No styles are available in this group.'}</p>`;
    }

    renderGroups() { if (this.openRow) this.fillDropdown(); }
    updateSwatches() {}

    renderSelection() {
      if (!this.variants || !this.tiers) return;
      this.renderAvailability();
      this.selection = pairSelection(this.pairs, this.quantity, this.variants);
      this.ready = !this.availabilityBlocked && [...this.selection.values()].reduce((sum,n) => sum+n,0) === this.quantity;
      this.querySelectorAll('[data-bp2-tier]').forEach(card => {
        const n = Number(card.dataset.bp2Tier), active = n === this.quantity;
        const quote = offerQuote(this.pairs,n,this.variants,this.tiers,this.addon);
        card.classList.toggle('on',active);
        const header = card.querySelector('.tier-head');
        header.setAttribute('aria-expanded',String(active)); header.disabled = this.busy || Boolean(this.pending);
        card.querySelector('.tier-body').inert = !active;
        card.querySelector('.now').textContent = `${quote.complete ? '' : 'From '}${this.money.format(quote.total/100)}`;
        card.querySelector('.was').textContent = quote.total < quote.subtotal ? this.money.format(quote.subtotal/100) : '';
        card.querySelector('.pp').textContent = `${this.money.format(quote.total/n/100)} / pair`;
        card.querySelectorAll('[data-bp2-row]').forEach(row => {
          const index = Number(row.dataset.bp2Row), variant = this.variants.get(this.pairs.get(index));
          const button = row.querySelector('.ptrig');
          button.disabled = this.busy || Boolean(this.pending);
          row.classList.toggle('bp2-empty',!variant);
          const price = variant ? addonPricing({...tierPricing(variant.price,n,this.tiers),subtotal:variant.price},{...this.addon,price:0}).total : 0;
          button.innerHTML = `${variant ? `<img src="${offerEscape(variant.image)}" alt="">` : '<span class="bp2-placeholder" aria-hidden="true">+</span>'}<span><span class="nm">${variant ? offerEscape(variant.title) : 'Choose your style'}</span><span class="sb">${variant ? `${offerGroupLabel(variant)} · <b>${this.money.format(price/100)}</b>${price < variant.price ? ` <s>${this.money.format(variant.price/100)}</s>` : ''}` : 'One Size'}</span>${variant?.preorder ? `<span class="bp2-preorder">Pre-order · ${offerEscape(dispatchLabel(variant.dispatch))}</span>` : ''}</span>${OFFER_ICONS.chev}`;
          button.setAttribute('aria-label',`Pair ${index + 1}: ${variant ? variant.title : 'Choose your style'}`);
          const preview = row.querySelector('.bp2-view-design');
          preview.hidden = !variant;
          preview.innerHTML = variant ? `<img src="${offerEscape(variant.image)}" alt=""><span>${DESIGN_ZOOM_ICON}</span>` : '';
          preview.dataset.bp2Preview = variant?.id || '';
          preview.setAttribute('aria-label', `View design for pair ${index + 1}: ${variant?.title || ''}`);
        });
      });
      this.renderGifts();
      const earlyAccessLocked = this.earlyAccessLocked;
      if (earlyAccessLocked && this.addon.type === 'membership') {
        this.addon.selected = false;
        this.setAddonReveal(false);
        const checkbox = this.querySelector('[data-bp-addon-toggle]');
        if (checkbox) checkbox.checked = false;
      }
      const earlyAccessCopy = this.querySelector('[data-bp-early-access-copy]');
      if (earlyAccessCopy) earlyAccessCopy.hidden = !earlyAccessLocked;
      const addonCard = this.querySelector('[data-bp-addon-card]');
      if (addonCard) addonCard.hidden = earlyAccessLocked;
      const quote = offerQuote(this.pairs,this.quantity,this.variants,this.tiers,this.addon);
      this.renderPurchaseNotices(quote);
      const add = this.querySelector('[data-bp-add]'); add.disabled = this.availabilityBlocked || this.busy || Boolean(this.pending);
      if (earlyAccessLocked && !this.busy) {
        add.textContent = 'EARLY ACCESS';
        add.setAttribute('aria-label', 'EARLY ACCESS');
      } else if (this.ready && !this.busy) {
        const preorderOnly = [...this.selection.keys()].every(id => this.variants.get(id)?.preorder);
        const earlyAccess = requiresEarlyAccess([...this.selection.keys()].map(id => this.variants.get(id)), false, this.dataset.pageEarlyAccess === 'true');
        const label = (earlyAccess ? 'EARLY ACCESS' : preorderOnly ? 'Pre-order' : this.dataset.buttonLabel || 'Add to cart').toLocaleUpperCase();
        // Compare against the undiscounted merchandise total, without adding gift retail values.
        add.innerHTML = `<span>${offerEscape(label)}</span><span class="bp-add-prices">– ${quote.total < quote.subtotal ? `<s>${offerEscape(this.money.format(quote.subtotal/100))}</s>` : ''}<strong>${offerEscape(this.money.format(quote.total/100))}</strong></span>`;
        add.setAttribute('aria-label',`${label}: ${this.quantity} ${this.quantity === 1 ? 'pair' : 'pairs'}, ${this.money.format(quote.total/100)}${quote.total < quote.subtotal ? `, originally ${this.money.format(quote.subtotal/100)}` : ''}`);
      } else {
        add.textContent = this.busy ? 'Adding to cart…' : this.quantity === 1 ? 'Choose your style' : 'Choose your styles';
        add.removeAttribute('aria-label');
      }
      const toggle = this.querySelector('[data-bp-addon-toggle]'); if (toggle) toggle.disabled = this.busy || Boolean(this.pending) || !this.addon.available;
      this.renderDetail(); this.renderStickyCart();
    }

    renderPurchaseNotices(quote) {
      const row = this.querySelector('[data-bp-shipping]');
      if (row) {
        const selected = [...this.selection.keys()].map(id => this.variants.get(id)).filter(Boolean);
        const estimate = shippingEstimate(selected, new Date(), Number(row.dataset.dispatchDays));
        row.querySelector('[data-bp-shipping-label]').textContent = estimate.label;
        row.querySelector('[data-bp-shipping-date]').textContent = estimate.date;
        row.hidden = false;
      }
      const sale = this.querySelector('[data-bp-sale-notice]');
      if (sale) sale.hidden = !(quote.discount > 0);
    }

    renderGifts() {
      const benefits = this.giftBenefits;
      const progress = giftProgress(benefits, this.quantity, this.tiers);
      this.querySelector('[data-bp2-gifts-title]').textContent = progress.unlocked ? 'Gifts unlocked' : 'Unlock free gifts';
      this.querySelector('[data-bp2-gifts]').innerHTML = benefits.map((gift,index) => {
        const unlocked = this.quantity >= gift.unlock;
        return `${index ? '<span class="gift-plus" aria-hidden="true">+</span>' : ''}<div class="gift ${unlocked ? '' : 'locked'}"><div class="pic">${gift.shipping ? OFFER_ICONS.ship : `<img src="${offerEscape(gift.image)}" alt="" loading="lazy">`}${unlocked ? '' : `<span class="gift-lock" aria-hidden="true">${OFFER_ICONS.lock}</span>`}</div>${unlocked ? `<span class="gift-value">${gift.value > 0 ? `<s>${this.money.format(gift.value/100)}</s>` : 'FREE'}</span><span class="gn">${offerEscape(gift.name)}</span>` : `<span class="gn">Locked<span class="gift-sr">: ${offerEscape(gift.name)}. Unlocks at ${gift.unlock} pairs.</span></span>`}</div>`;
      }).join('');
      this.querySelector('[data-bp2-gift-message]').innerHTML = !benefits.length ? '' : progress.unlocked === progress.total ? 'Both included free with your bundle.' : progress.next ? `<button type="button" data-bp2-size="${progress.next}">Choose ${progress.next} pairs</button> to unlock ${progress.nextUnlocksAll ? 'both gifts' : 'your first gift'}.` : 'Choose a qualifying bundle to unlock your gifts.';
    }

    focusSelection() {
      if (this.availabilityBlocked || this.busy || this.pending) return;
      const index = Array.from({length:this.quantity},(_,i)=>i).find(i=>!this.variants.get(this.pairs.get(i))?.available) ?? 0;
      const prompt = this.querySelector('[data-bp-pair-prompt]'); prompt.hidden = false; prompt.textContent = 'Please pick your styles first.';
      const row = this.querySelector(`[data-bp2-tier="${this.quantity}"] [data-bp2-row="${index}"]`);
      row.before(prompt);
      row.querySelector('.ptrig').focus({preventScroll:true}); this.openDropdown(row);
      if (!matchMedia('(max-width: 767px)').matches) row.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',block:'center'});
    }

    viewVariant(id) {
      const variant = this.variants.get(id);
      if (!variant) return;
      this.viewId = id;
      this.syncHeroGallery();
      this.updateSwatches();
    }

    syncHeroGallery() {
      if (!this.initialGallery) return;
      const variant = this.variants?.get(this.viewId);
      if (this.shoppingAlternatives || this.availabilityBlocked || window.matchMedia('(max-width: 767px)').matches || !variant) {
        // Leave the opening slides and current slide position untouched on mobile.
        // Restore them once if the shopper resized from a desktop design gallery.
        if (!this.showingOpeningGallery) {
          this.showingOpeningGallery = true;
          this.setGallery(this.initialGallery);
        }
      } else {
        this.showingOpeningGallery = false;
        this.setGallery(variant.gallery.length ? variant.gallery : [{ src: variant.image, alt: variant.title }]);
      }
      this.renderDetail();
    }

    openDesignPreview(id, opener) {
      const variant = this.variants?.get(id);
      if (!variant || this.designDialog?.open) return;
      this.closeDropdown(false, false);
      const images = (variant.gallery.length ? variant.gallery : [{src:variant.image, alt:variant.title}]).filter(image => image.src);
      if (!images.length) return;
      const pair = Number(opener.closest('[data-bp2-row]').dataset.bp2Row);
      if (!this.designDialog) this.createDesignPreview();
      const dialog = this.designDialog;
      // The chosen-pair thumbnail opens the full gallery without stacking selection dialogs.
      this.designPreview = {id, pair, opener, images, index:0, zoomed:false, scrollX:window.scrollX, scrollY:window.scrollY};
      dialog.querySelector('[data-design-title]').textContent = variant.title;
      const action = dialog.querySelector('[data-design-choose]');
      action.textContent = this.pairs.get(pair) === id ? 'Back to my bundle' : 'Choose';
      action.disabled = !variant.available || this.busy || Boolean(this.pending);
      this.populateDesignPhotos(dialog, variant, images);
      // Fixed-body locking preserves the eighth pair's position on mobile Safari.
      Object.assign(this.designPreview, this.lockPageScroll());
      dialog.showModal();
      dialog.querySelector('[data-design-track]').scrollLeft = 0;
      dialog.querySelector('[data-design-close]').focus({preventScroll:true});
    }

    populateDesignPhotos(dialog, variant, images) {
      const track = dialog.querySelector('[data-design-track]');
      track.replaceChildren(...images.map((image, index) => {
        const slide = this.button('', 'bp2-design-photo');
        slide.dataset.designPhoto = index;
        slide.setAttribute('aria-label', `Zoom photo ${index + 1} of ${images.length}`);
        const img = document.createElement('img');
        img.src = image.src; img.alt = image.alt || variant.title; img.draggable = false;
        img.loading = index === 0 ? 'eager' : 'lazy';
        slide.append(img); return slide;
      }));
      dialog.querySelector('[data-design-dots]').replaceChildren(...images.map((image, index) => {
        const dot = this.button('', 'bp-gallery-dot');
        dot.dataset.designDot = index;
        dot.setAttribute('aria-label', `Show design photo ${index + 1} of ${images.length}`);
        return dot;
      }));
      dialog.querySelector('[data-design-zoom-image]').removeAttribute('src');
      dialog.querySelector('[data-design-zoom-area]').hidden = true;
      this.updateDesignControls();
    }

    designStageMarkup() {
      return `<div class="bp2-design-stage">
          <div class="bp2-design-track" data-design-track aria-label="Design photos"></div>
          <div class="bp2-design-zoom-area" data-design-zoom-area hidden tabindex="0" aria-label="Zoomed photo. Scroll to see details."><img data-design-zoom-image alt=""></div>
          <div class="bp-gallery-nav" data-design-nav>
            <button type="button" class="bp-gallery-arrow bp-gallery-arrow--previous" data-design-prev aria-label="Previous design photo"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 5-7 7 7 7"/></svg></button>
            <button type="button" class="bp-gallery-arrow bp-gallery-arrow--next" data-design-next aria-label="Next design photo"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m10 5 7 7-7 7"/></svg></button>
            <div class="bp-gallery-dots" data-design-dots role="group" aria-label="Choose design photo"></div>
          </div>
          <button type="button" class="bp2-design-zoom" data-design-zoom aria-label="Zoom in" aria-pressed="false"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5M7.5 10.5h6"/><path class="bp2-zoom-plus" d="M10.5 7.5v6"/></svg></button>
        </div>
        <span class="bp2-design-status" data-design-count aria-live="polite"></span>`;
    }

    createDesignPreview() {
      const dialog = document.createElement('dialog');
      dialog.className = 'bp2-design-dialog';
      const titleId = `bp2-design-title-${this.dataset.catalog}`;
      dialog.setAttribute('aria-labelledby',titleId);
      dialog.innerHTML = `<div class="bp2-design-shell">
        <header class="bp2-design-header"><div><h2 id="${offerEscape(titleId)}" data-design-title></h2></div><button type="button" data-design-close aria-label="Close design preview" autofocus><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button></header>
        ${this.designStageMarkup()}
        <button type="button" class="bp2-design-choose" data-design-choose></button>
      </div>`;
      this.append(dialog); this.designDialog = dialog;
      dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeDesignPreview(); }, {signal:this.abort.signal});
      dialog.addEventListener('click', event => {
        event.stopPropagation();
        if (event.target === dialog || event.target.closest('[data-design-close]')) { this.closeDesignPreview(); return; }
        const state = this.designPreview;
        if (!state) return;
        if (event.target.closest('[data-design-prev]')) this.showDesignPhoto(state.index - 1);
        else if (event.target.closest('[data-design-next]')) this.showDesignPhoto(state.index + 1);
        else if (event.target.closest('[data-design-dot]')) this.showDesignPhoto(Number(event.target.closest('[data-design-dot]').dataset.designDot));
        else if (event.target.closest('[data-design-zoom], [data-design-photo]')) this.toggleDesignZoom();
        else if (event.target.closest('[data-design-choose]')) {
          if (this.busy || this.pending || !this.variants.get(state.id)?.available) return;
          if (!this.selectDesign(state.pair, state.id)) return;
          state.opener = this.querySelector(`[data-bp2-tier="${this.quantity}"] [data-bp2-row="${state.pair}"] .bp2-view-design`);
          this.closeDesignPreview();
        }
      }, {signal:this.abort.signal});
      dialog.addEventListener('keydown', event => {
        if (!this.designPreview?.zoomed && ['ArrowLeft','ArrowRight'].includes(event.key)) {
          event.preventDefault(); this.showDesignPhoto(this.designPreview.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, {signal:this.abort.signal});
      const track = dialog.querySelector('[data-design-track]');
      track.addEventListener('scroll', () => {
        if (!this.designPreview || !track.clientWidth) return;
        this.designPreview.index = Math.max(0,Math.min(this.designPreview.images.length - 1,Math.round(track.scrollLeft / track.clientWidth)));
        this.updateDesignControls();
      }, {passive:true,signal:this.abort.signal});
    }

    updateDesignControls() {
      const state = this.designPreview, dialog = state?.inline ? this.pickerDialog : this.designDialog;
      dialog.querySelector('[data-design-count]').textContent = `Photo ${state.index + 1} of ${state.images.length}`;
      const previous = dialog.querySelector('[data-design-prev]'), next = dialog.querySelector('[data-design-next]');
      previous.hidden = state.index === 0;
      next.hidden = state.index === state.images.length - 1;
      dialog.querySelector('[data-design-nav]').hidden = state.images.length < 2;
      dialog.querySelectorAll('[data-design-dot]').forEach(dot => dot.setAttribute('aria-current',String(Number(dot.dataset.designDot) === state.index)));
      if ((document.activeElement === previous && previous.hidden) || (document.activeElement === next && next.hidden)) {
        dialog.querySelector(`[data-design-dot="${state.index}"]`).focus({preventScroll:true});
      }
    }

    showDesignPhoto(index) {
      const state = this.designPreview;
      if (index < 0 || index >= state.images.length) return;
      if (state.zoomed) this.toggleDesignZoom();
      const track = (state.inline ? this.pickerDialog : this.designDialog).querySelector('[data-design-track]');
      track.scrollTo({left:index * track.clientWidth,behavior:'instant'});
      state.index = index; this.updateDesignControls();
    }

    toggleDesignZoom() {
      const state = this.designPreview, dialog = state?.inline ? this.pickerDialog : this.designDialog;
      state.zoomed = !state.zoomed;
      const area = dialog.querySelector('[data-design-zoom-area]');
      area.hidden = !state.zoomed;
      dialog.querySelector('[data-design-track]').inert = state.zoomed;
      dialog.querySelector('[data-design-zoom]').setAttribute('aria-pressed',String(state.zoomed));
      dialog.querySelector('[data-design-zoom]').setAttribute('aria-label',state.zoomed ? 'Zoom out' : 'Zoom in');
      if (state.zoomed) {
        const img = dialog.querySelector('[data-design-zoom-image]');
        const source = new URL(state.images[state.index].src,location.href);
        // Use Shopify's larger original photo only when someone asks to inspect it.
        if (source.hostname === location.hostname || source.hostname.endsWith('.shopify.com')) source.searchParams.set('width','2400');
        img.src = source.href; img.alt = state.images[state.index].alt || dialog.querySelector('[data-design-title]').textContent;
        area.scrollLeft = area.clientWidth / 2; area.scrollTop = area.clientHeight / 2;
      }
    }

    closeDesignPreview(restoreFocus = true) {
      const state = this.designPreview;
      if (!state) return;
      if (state.inline) {this.backToPicker(); return;}
      this.designDialog.close(); this.designPreview = null;
      this.restorePageScroll(state);
      if (restoreFocus && state.opener?.isConnected) state.opener.focus({preventScroll:true});
      this.designDialog.querySelector('[data-design-track]').inert = false;
      this.designDialog.querySelector('[data-design-zoom]').setAttribute('aria-pressed','false');
      this.designDialog.querySelector('[data-design-zoom]').setAttribute('aria-label','Zoom in');
    }

    button(text, className) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = className; button.textContent = text;
      return button;
    }

    renderDetail() {
      const variant = this.variants?.get(this.viewId);
      this.querySelector('[data-bp-hero-preorder]').hidden = this.showingOpeningGallery || !variant?.preorder;
      const detail = this.querySelector('[data-bp-unit-price]');
      // The merchant can hide the standalone price block without disabling the builder.
      if (!detail) return;
      detail.hidden = !variant;
      if (!variant) return;
      // Preview the chosen quantity's tier without filling any empty pair slots.
      const tierPrice = tierPricing(variant.price, this.quantity, this.tiers);
      const pricing = addonPricing({ ...tierPrice, subtotal: variant.price }, { ...this.addon, price: 0 });
      detail.querySelector('[data-bp-price-name]').textContent = variant.title;
      detail.querySelector('[data-bp-price-current]').textContent = this.money.format(pricing.total / 100);
      const compare = detail.querySelector('[data-bp-price-compare]');
      compare.textContent = this.money.format(variant.price / 100);
      compare.hidden = pricing.total >= variant.price;
    }

    initializeStickyCart() {
      const sticky = this.querySelector('[data-bp-sticky-cart]');
      if (!sticky) return;
      this.stickyObserver?.disconnect();
      this.stickyObserver = new IntersectionObserver(([entry]) => {
        // Only show after the main button has passed above the viewport, never before it.
        const visible = !this.availabilityBlocked && !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', String(!visible));
        sticky.inert = !visible;
      }, { threshold: 0 });
      this.stickyObserver.observe(this.querySelector('[data-bp-add]'));
    }

    renderStickyCart() {
      const sticky = this.querySelector('[data-bp-sticky-cart]');
      if (!sticky) return;
      const main = this.querySelector('[data-bp-add]'), button = sticky.querySelector('[data-bp-sticky-add]');
      button.disabled = main.disabled;
      button.replaceChildren(...Array.from(main.childNodes, node => node.cloneNode(true)));
      button.setAttribute('aria-label', main.getAttribute('aria-label') || main.textContent);
      button.setAttribute('aria-busy', String(this.busy));
    }

    updateCountdown() {
      const holder = this.querySelector('[data-bp-countdown-wrap]');
      if (!holder) return;
      const { remaining, sold } = urgencyState({ mode: this.dataset.urgencyMode, deadline: this.dataset.deadline, duration: this.dataset.urgencyDuration, sold: this.dataset.soldPercentage }, this.urgencyStartedAt);
      const soldText = this.querySelector('[data-bp-sold-percentage]');
      const progress = this.querySelector('[data-bp-urgency-progress]');
      if (soldText && soldText.textContent !== `${sold}% sold`) {
        soldText.textContent = `${sold}% sold`;
        if (progress) progress.style.width = `${sold}%`;
        soldText.classList.remove('is-updated');
        void soldText.offsetWidth;
        soldText.classList.add('is-updated');
      }
      holder.hidden = remaining === null;
      if (remaining === null) return;
      const hours = Math.floor(remaining / 3600), minutes = Math.floor(remaining % 3600 / 60), seconds = remaining % 60;
      const ended = remaining === 0 && this.dataset.urgencyMode !== 'bundle-new';
      holder.querySelector('[data-bp-countdown]').textContent = ended ? 'Ended' : [hours, minutes, seconds].map(value => String(value).padStart(2, '0')).join(':');
      holder.querySelector('[data-bp-countdown-label]').hidden = ended;
      if (ended) clearInterval(this.countdownInterval);
    }

    destroyGallery() {
      this.gallerySlider?.destroy();
      this.gallerySlider = null;
    }

    resizeGallery() {
      if (!this.galleryNeedsResize || !this.gallerySlider || this.gallerySlider.isAnimating || this.gallerySlider.isDragging) return;
      this.galleryNeedsResize = false;
      this.gallerySlider.resize();
    }

    setGallery(images) {
      this.gallery = images.filter(image => image.src);
      this.destroyGallery();
      this.galleryAbort?.abort();
      this.galleryAbort = new AbortController();
      const slider = this.querySelector('[data-bp-slider]');
      this.querySelector('[data-bp-gallery-empty]').hidden = this.gallery.length > 0;
      slider.replaceChildren(...this.gallery.map((image, index) => {
        const slide = document.createElement('div'); slide.className = 'product-single__media-slide';
        slide.setAttribute('role', 'group'); slide.setAttribute('aria-label', `Image ${index + 1} of ${this.gallery.length}`);
        const media = document.createElement('div'); media.className = 'product-single__media product-single__media--image';
        media.style.aspectRatio = String(image.ratio || 1);
        const img = document.createElement('img'); img.className = 'bp-hero'; img.src = image.src; img.alt = image.alt || 'Elliosa grip socks';
        img.loading = index ? 'lazy' : 'eager'; img.draggable = false;
        media.append(img); slide.append(media); return slide;
      }));
      const navigation = this.querySelector('[data-bp-gallery-nav]');
      navigation.hidden = this.gallery.length < 2;
      navigation.querySelector('[data-bp-image-dots]').replaceChildren(...this.gallery.map((image, index) => {
        const dot = this.button('', 'bp-gallery-dot'); dot.dataset.bpImage = String(index);
        dot.setAttribute('aria-label', `Show product image ${index + 1} of ${this.gallery.length}`);
        return dot;
      }));
      const thumbnails = this.querySelector('[data-bp-thumbnails]');
      if (thumbnails) {
        thumbnails.hidden = this.gallery.length < 2;
        thumbnails.replaceChildren(...this.gallery.map((image, index) => {
          const thumbnail = this.button('', 'bp2-thumbnail'); thumbnail.dataset.bpImage = String(index);
          thumbnail.setAttribute('aria-label', `View image ${index + 1} of ${this.gallery.length}: ${image.alt || 'Elliosa grip socks'}`);
          const img = document.createElement('img'); img.src = image.src; img.alt = ''; img.loading = 'lazy'; img.draggable = false;
          thumbnail.append(img); return thumbnail;
        }));
      }
      if (!this.gallery.length) { this.imageIndex = 0; return; }
      // Keep the default gallery engine and dimensions; custom controls also work with scroll-snap.
      const Flickity = window.themeVendor?.Flickity;
      if (Flickity && this.gallery.length > 1) {
        this.gallerySlider = new Flickity(slider, {
          wrapAround: false, pageDots: false, prevNextButtons: false, adaptiveHeight: true,
          on: { change: index => this.updateImageSelection(index), settle: () => this.resizeGallery() }
        });
      } else {
        slider.addEventListener('scroll', () => {
          const index = Math.round(slider.scrollLeft / slider.clientWidth);
          if (this.gallery[index]) this.updateImageSelection(index);
        }, { passive: true, signal: this.galleryAbort.signal });
      }
      this.showImage(0, true);
      this.resizeGallery();
    }

    showImage(index, instant = false) {
      if (!this.gallery?.[index]) return;
      if (this.gallerySlider) this.gallerySlider.select(index, false, instant);
      else {
        const slider = this.querySelector('[data-bp-slider]');
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        slider.scrollTo({ left: index * slider.clientWidth, behavior: instant || reduceMotion ? 'instant' : 'smooth' });
      }
      this.updateImageSelection(index);
    }

    updateImageSelection(index) {
      this.imageIndex = index;
      this.querySelectorAll('[data-bp-image]').forEach(dot => {
        if (Number(dot.dataset.bpImage) === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const thumbnails = this.querySelector('[data-bp-thumbnails]');
      const thumbnail = thumbnails?.querySelector('[aria-current="true"]');
      if (thumbnail) {
        const listBounds = thumbnails.getBoundingClientRect(), imageBounds = thumbnail.getBoundingClientRect();
        if (imageBounds.left < listBounds.left || imageBounds.right > listBounds.right) {
          thumbnails.scrollTo({left:thumbnails.scrollLeft + imageBounds.left - listBounds.left - (thumbnails.clientWidth - imageBounds.width) / 2, behavior:'instant'});
        }
      }
      const previous = this.querySelector('[data-bp-image-prev]');
      const next = this.querySelector('[data-bp-image-next]');
      previous.hidden = index === 0;
      next.hidden = index === this.gallery.length - 1;
      // Keep keyboard focus usable when the arrow just clicked reaches an end.
      if (document.activeElement === previous && previous.hidden || document.activeElement === next && next.hidden) {
        this.querySelector(`[data-bp-image="${index}"]`)?.focus({ preventScroll: true });
      }
    }

    message(text, error = false) {
      const prompt = this.querySelector('[data-bp-pair-prompt]');
      if (prompt) { prompt.hidden = true; prompt.textContent = ''; }
      this.querySelectorAll('[data-bp-message], [data-bp-sticky-message]').forEach(node => { node.textContent = text; node.classList.toggle('is-error', error); node.classList.toggle('bp-status-only', !error); });
    }
    savePending(attempt) {
      this.pending = attempt;
      try { if (attempt) sessionStorage.setItem(this.storageKey, JSON.stringify(attempt)); else sessionStorage.removeItem(this.storageKey); } catch { /* Keep the in-memory guard. */ }
    }

    get selectionStorageKey() { return `${this.storageKey}:selection:${location.pathname}`; }

    saveSelection() {
      if (window.Shopify?.designMode) return;
      try {
        // Store identities and slot positions only; prices and availability come from the fresh catalog.
        window.localStorage.setItem(this.selectionStorageKey, JSON.stringify({
          version: 1, savedAt: Date.now(), quantity: this.quantity, pairs: [...this.pairs],
          shoppingAlternatives: this.shoppingAlternatives === true,
          addon: {selected: this.addon.selected, id: this.addon.id, type: this.addon.type}
        }));
      } catch { /* Storage restrictions must not interrupt choosing or purchasing. */ }
    }

    restoreSelection() {
      if (this.selectionRestored || !this.variants.size || window.Shopify?.designMode) return;
      this.selectionRestored = true;
      try {
        const raw = window.localStorage.getItem(this.selectionStorageKey);
        if (!raw) return;
        const saved = restoreOfferSelection(JSON.parse(raw), this.tiers, this.variants, this.addon);
        if (!saved) { this.clearSavedSelection(); return; }
        // Old product drafts must not bypass a newly sold-out/coming-soon notice.
        if (this.dataset.pageAvailability && !saved.shoppingAlternatives) return;
        this.shoppingAlternatives = saved.shoppingAlternatives;
        this.quantity = saved.quantity;
        this.pairs = saved.pairs;
        this.addon.selected = saved.addonSelected;
        const toggle = this.querySelector('[data-bp-addon-toggle]');
        if (toggle) toggle.checked = saved.addonSelected;
        this.setAddonReveal(saved.addonSelected && this.addon.type === 'membership');
      } catch { this.clearSavedSelection(); }
    }

    clearSavedSelection() {
      if (window.Shopify?.designMode) return;
      try { window.localStorage.removeItem(this.selectionStorageKey); } catch { /* Storage is optional. */ }
    }

    showPending(partial = false) {
      this.message(partial ? 'Only part of your bundle is in the cart. Open your cart to review it. To start again, remove those bundle items and check the cart here before adding again.' : 'Please check your cart before adding again. We could not confirm the previous update.', true);
      this.querySelector('[data-bp-cart-link]').hidden = false;
      this.querySelector('[data-bp-check-cart]').hidden = false;
      this.querySelector('[data-bp-add]').disabled = true;
      this.renderStickyCart();
    }

    async checkPending() {
      if (!this.pending || this.busy) return;
      this.busy = true;
      try {
        const cart = await this.getCart();
        const status = pendingCartStatus(cart, this.pending);
        if (status === 'complete') { await this.completeCartAdd(); return; }
        if (status === 'partial') { this.showPending(true); return; }
        this.savePending(null);
        this.querySelector('[data-bp-cart-link]').hidden = true;
        this.querySelector('[data-bp-check-cart]').hidden = true;
        this.message('This bundle is not in your cart. You can now add your selection.');
      } catch { this.message('Your cart could not be checked. Open your cart before trying again.', true); }
      finally { this.busy = this.navigating; this.renderSelection(); }
    }

    watchUpCart(attempt) {
      this.stopWatchingUpCart?.();
      this.upcartSyncedRequest = null;
      const observe = event => {
        if (event?.cart && pendingCartStatus(event.cart, attempt) === 'complete') this.upcartSyncedRequest = attempt.id;
      };
      const unsubscribers = [];
      for (const subscribe of ['upcartSubscribeCartUpdated', 'upcartSubscribeCartLoaded']) {
        try {
          if (typeof window[subscribe] === 'function') unsubscribers.push(window[subscribe](observe));
        } catch { /* Public refresh remains available if subscriptions are not ready. */ }
      }
      this.stopWatchingUpCart = () => {
        unsubscribers.splice(0).forEach(unsubscribe => { try { if (typeof unsubscribe === 'function') unsubscribe(); } catch { /* Do not block button cleanup. */ } });
      };
    }

    async completeCartAdd() {
      const attempt = this.pending;
      const giftFailed = await settleOfferGifts(attempt, () => this.getCart(), body => this.updateCart(body));
      // Match bundle-new: a confirmed cart addition completes the saved draft.
      this.clearSavedSelection();
      if (attempt?.offerCodes?.length) this.upcartSyncedRequest = null;
      this.savePending(null);
      this.querySelector('[data-bp-cart-link]').hidden = true;
      this.querySelector('[data-bp-check-cart]').hidden = true;
      this.message(giftFailed ? 'Your socks were added, but the free gift could not be applied. It has been removed so you will not be charged for it. Please contact us about the offer.' : 'Your bundle was added to your cart.', giftFailed);
      let opened = false;
      try { opened = await refreshAndOpenUpCart(window, attempt, this.upcartSyncedRequest); }
      catch { /* The add succeeded even if the drawer could not refresh. */ }
      if (attempt?.membership) {
        let result = 'unavailable';
        try { result = await ensureMembershipCart(() => document.querySelector('.exison-widget__Containe_Wrapper .col-member'), () => this.getCart(), attempt.membershipVariantId || this.addon.id); }
        catch { /* Socks are already added; never repeat their request for a membership failure. */ }
        if (result === 'unavailable') {
          this.message('Your socks were added. Membership could not be selected automatically; choose Elliosa+ in the cart.', true);
          this.querySelector('[data-bp-cart-link]').hidden = false;
        }
      }
      try {
        if (await reconcileOfferGifts(attempt, () => this.getCart(), body => this.updateCart(body))) {
          await refreshAndOpenUpCart(window);
        }
      } catch {
        this.message('Your socks were added. Please review the free gifts in your cart.', true);
        this.querySelector('[data-bp-cart-link]').hidden = false;
      }
      if (!opened) {
        this.message('Your bundle was added. Open your cart to review it.', true);
        this.querySelector('[data-bp-cart-link]').hidden = false;
      }
    }

    async updateCart(body) {
      const response = await fetch(`${this.dataset.cartUrl}/update.js`, {method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(body)});
      if (!response.ok) throw new Error('The gift discount could not be confirmed. Please check your cart.');
      return response.json();
    }

    async getCart() {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Could not read the cart.');
      return response.json();
    }

    async refreshRejectedSelection(selection) {
      const selected = [...selection.keys()].map(id => this.variants.get(id)).filter(variant => variant?.handle);
      const handles = [...new Set(selected.map(variant => variant.handle))];
      const unavailable = new Set();
      // Refresh only after a confirmed rejection with no added items. Normal adds
      // keep one request, and ambiguous/partial additions retain their recovery guard.
      await Promise.all(handles.map(async handle => {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {headers:{Accept:'application/json'},cache:'no-store'});
          if (!response.ok && response.status !== 404) return;
          const product = response.ok ? await response.json() : null;
          const tags = Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',').map(tag => tag.trim());
          for (const variant of selected.filter(item => item.handle === handle)) {
            const fresh = product?.variants?.find(item => String(item.id) === variant.id);
            if (!fresh?.available || (tags.includes('coming-soon') && !(tags.includes('pre-order') && variant.preorder))) unavailable.add(variant.id);
          }
        } catch { /* Preserve choices if fresh availability cannot be confirmed. */ }
      }));
      if (!unavailable.size) return '';
      const names = [...new Set([...unavailable].map(id => this.variants.get(id).title))];
      for (const id of unavailable) this.variants.get(id).available = false;
      this.pairs = new Map([...this.pairs].filter(([,id]) => !unavailable.has(id)));
      this.saveSelection();
      this.renderAvailability();
      return `${names.join(', ')} ${names.length === 1 ? 'is' : 'are'} no longer available. Please choose a replacement; your other choices are saved.`;
    }

    async addToCart() {
      if (this.earlyAccessLocked) { this.openEarlyAccess(); return; }
      if (this.availabilityBlocked || this.busy || this.pending || !this.quantityValid) return;
      const selection = pairSelection(this.pairs, this.quantity, this.variants);
      if (bundleTotals(selection, this.variants, this.tiers).count !== this.quantity) return;
      this.busy = true;
      const attempt = { id: crypto.randomUUID(), items: [] };
      let submitted = false;
      try {
        // The gift tiles are promotional only. UpCart grants the wash bag using its own rules.
        attempt.items = appendAddonItem(buildCartItems(selection, this.variants, attempt.id), this.addon, attempt.id);
        this.closeDropdown();
        attempt.paidGifts = false;
        attempt.offerCodes = this.giftsEnabled && this.giftBenefits.some(gift => gift.shipping && this.quantity >= gift.unlock) ? [this.dataset.freeShippingCode].filter(Boolean) : [];
        attempt.membership = this.addon.selected && this.addon.type === 'membership';
        attempt.membershipVariantId = this.addon.id;
        this.watchUpCart(attempt);
        this.savePending(attempt); this.renderSelection();
        this.querySelector('[data-bp-add]').setAttribute('aria-busy', 'true');
        submitted = true;
        const response = await fetch(this.dataset.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: JSON.stringify({ items: attempt.items }) });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          const cart = await this.getCart();
          const status = pendingCartStatus(cart, attempt);
          if (status === 'complete') { await this.completeCartAdd(); return; }
          if (status === 'partial') { this.showPending(true); return; }
          this.savePending(null);
          const replacement = await this.refreshRejectedSelection(selection);
          throw new Error(replacement || error.description || 'Some selected items are no longer available. Please review your choices.');
        }
        await this.completeCartAdd();
      } catch (error) {
        if (submitted && this.pending) {
          try {
            const cart = await this.getCart();
            const status = pendingCartStatus(cart, attempt);
            if (status === 'complete') { await this.completeCartAdd(); return; }
            if (status === 'partial') { this.showPending(true); return; }
          } catch { /* Never retry an unconfirmed add automatically. */ }
          this.showPending();
        } else this.message(error.message || 'Your bundle could not be added.', true);
      } finally {
        this.stopWatchingUpCart?.();
        this.busy = this.navigating; this.renderSelection();
        this.querySelector('[data-bp-add]').removeAttribute('aria-busy');
      }
    }

    onEditorSelect(event) {
      const blockId = event.detail?.blockId;
      const tier = blockId && this.tiers?.find(offer => offer.blockId === blockId);
      if (tier) {
        this.closeDropdown();
        this.quantity = tier.quantity;
        this.renderSelection();
        this.querySelector(`[data-bp2-tier="${tier.quantity}"]`)?.scrollIntoView({block:'nearest'});
        return;
      }
      const gallery = this.querySelector('[data-bp-layout-area="gallery"]');
      const gallerySelected = blockId && blockId === gallery?.dataset.bpGalleryBlock;
      const imageIndex = this.initialGallery?.findIndex(image => image.blockId === blockId) ?? -1;
      if (gallerySelected || imageIndex >= 0) {
        this.showingOpeningGallery = true;
        this.setGallery(this.initialGallery);
        this.showImage(Math.max(0, imageIndex), true);
        this.renderDetail();
        gallery?.scrollIntoView({block:'nearest'});
        return;
      }
      const variant = Array.from(this.variants?.values() || []).find(item => item.blockId === event.detail?.blockId);
      if (variant) this.viewVariant(variant.id);
    }
  }
  customElements.define('elliosa-bundle-product-2', BundleProduct);
}
